import { useState, useEffect, useCallback, useMemo } from 'react';
import { MdOutlineHealthAndSafety, MdOutlineNotificationsActive } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import BottomNavigation from '../../components/feature/BottomNavigation';
import Button from '../../components/base/Button';
import PointsService, { type MemberPoints, type LevelTitle } from '../../services/pointsService';
import { QuizBadgeWrapper } from '../../components/feature/QuizNotificationBadge';
import QuizModal from '../../components/feature/QuizModal';
import QuizSummaryModal from '../../components/feature/QuizSummaryModal';
import TreatmentSummaryModal from '../../components/feature/TreatmentSummaryModal';
import FamilyService from '../../services/familyService';
import MedicationService from '../../services/medicationService';
import TreatmentSummaryService from '../../services/treatmentSummaryService';
import HealthHistoryModal from '../../components/feature/HealthHistoryModal';
import PointsHistoryModal from '../../components/feature/PointsHistoryModal';
import AchievementNotification from '../../components/feature/AchievementNotification';
import BadgeGallery from '../../components/feature/BadgeGallery';
import BadgeNotification from '../../components/feature/BadgeNotification';
import memberHeaderBg from '../../assets/member-header-bg.png';
import { useAuth } from '../../contexts/AuthContext';
import { usePrescriptionContext } from '../../contexts/PrescriptionContext';
import { useAchievements } from '../../hooks/useAchievements';
import { useBadges } from '../../hooks/useBadges';
import { adherenceService, type TreatmentAdherence } from '../../services/adherenceService';
import toast from '../../utils/toast';
import { supabase } from '../../lib/supabase';
import pushService from '../../services/pushService';
import { getTodayBRT, addDaysBRTFrom, nextMondayBRT, formatDateBRT } from '../../utils/dateUtils';
import { logger } from '../../utils/logger';
import { featureGateService } from '../../services/featureGateService';
import { UpgradeModal } from '../../components/payment/UpgradeModal';
import { useSubscription } from '../../hooks/useSubscription';

interface Member {
  id: string;
  name: string;
  avatar: string;
  streak: number;
  points: number;
  status: 'excellent' | 'good' | 'warning';
  todayDoses: {
    taken: number;
    total: number;
  };
  medications: Array<{
    id: string;
    medicationId?: string; // ID original da medicação (quando id tem sufixo _timeSlot)
    name: string;
    remedy_name: string;
    potency: string;
    dosage_type: 'globules' | 'drops';
    quantity: number;
    times: string[];
    frequency: string;
    start_date: string;
    end_date: string | null;
    usage_days: number;
    pause_days: number;
    total_period_months: number;
    notes: string | null;
    status: 'active' | 'paused' | 'completed' | 'upcoming';
    progress: number;
    daysElapsed: number;
    totalDays: number;
    taken: boolean;
    isPaused?: boolean;
    resumeDate?: string | null;
    isUpcoming?: boolean;
    daysUntilStart?: number;
    created_at?: string | null;
  }>;
  medicationsGrouped: Array<{
    id: string;
    name: string;
    remedy_name: string;
    potency: string;
    dosage_type: 'globules' | 'drops';
    quantity: number;
    times: string[];
    frequency: string;
    start_date: string;
    end_date: string | null;
    usage_days: number;
    pause_days: number;
    total_period_months: number;
    notes: string | null;
    status: 'active' | 'paused' | 'completed' | 'upcoming';
    progress: number;
    daysElapsed: number;
    totalDays: number;
    isPaused?: boolean;
    resumeDate?: string | null;
    isUpcoming?: boolean;
    daysUntilStart?: number;
    created_at?: string | null;
  }>;
  healthEvents: Array<{
    id: string;
    type: 'positive' | 'negative';
    description: string;
    date: string;
    category: 'treatment' | 'illness' | 'allopathic' | 'medical_record' | 'general';
    icon: string;
  }>;
  medicalRecords: Array<{
    id: string;
    type: 'illness' | 'allopathic_medication';
    description: string;
    date: string;
    severity?: 'low' | 'medium' | 'high';
    medicationType?: string;
  }>;
  reminders: Array<{
    id: string;
    title: string;
    description?: string;
    reminder_date?: string;
    reminder_time?: string;
    days_of_week?: number[];
    created_at: string;
  }>;
}

interface NewMedicalRecord {
  type: 'illness' | 'allopathic_medication';
  description: string;
  severity?: 'leve' | 'moderado' | 'alto';
  medicationType?: string;
}

interface NewHealthEvent {
  type: 'positive' | 'negative';
  description: string;
}

interface NewReminder {
  title: string;
  description: string;
  type: 'once' | 'recurring';
  reminder_date: string;
  reminder_time: string;
  days_of_week: number[];
}

interface NewTreatment {
  medicationName: string;
  potencyNumber: string;
  potencyType: 'DH' | 'CH' | 'M';
  dosageType: 'globules' | 'drops';
  dosage: string;
  startDate: string; // Nova propriedade para data de início
  times: string[];
  frequency: 'daily' | 'alternate' | 'weekly' | 'custom';
  customDays: string[];
  weeklyDayIndex: number | null; // 0=Dom, 1=Seg, ..., 6=Sáb (para weekly)
  usageDays: string;
  pauseDays: string;
  totalPeriod: string;
  notes: string;
}

const isImageAvatar = (avatar?: string) =>
  typeof avatar === 'string' &&
  (avatar.startsWith('/Imagens/') || avatar.startsWith('http') || avatar.startsWith('data:'));

export default function MemberDashboard() {
  const navigate = useNavigate();
  const { memberId } = useParams();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const pointsService = PointsService.getInstance();
  const familyService = FamilyService.getInstance();
  const [loading, setLoading] = useState(true);
  const [memberPoints, setMemberPoints] = useState<MemberPoints | null>(null);
  const [memberLevelTitle, setMemberLevelTitle] = useState<LevelTitle | null>(null);
  const [memberAdherence, setMemberAdherence] = useState<TreatmentAdherence | null>(null);

  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [showAddMedicalModal, setShowAddMedicalModal] = useState(false);
  const [showAddReminderModal, setShowAddReminderModal] = useState(false);
  const [showAllRemindersModal, setShowAllRemindersModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState<any>(null);
  const [showTreatmentModal, setShowTreatmentModal] = useState(false);
  const [savingTreatment, setSavingTreatment] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [showQuizSummaryModal, setShowQuizSummaryModal] = useState(false);
  const [showTreatmentSummaryModal, setShowTreatmentSummaryModal] = useState(false);
  const [summaryMedicationId, setSummaryMedicationId] = useState<string | null>(null);
  const [autoSummaryOpened, setAutoSummaryOpened] = useState(false);
  const [showHealthHistoryModal, setShowHealthHistoryModal] = useState(false);
  const [showBadgeGallery, setShowBadgeGallery] = useState(false);
  const [badgesCount, setBadgesCount] = useState(0);
  const [adherenceMap, setAdherenceMap] = useState<Record<string, number>>({});
  
  const [newReminder, setNewReminder] = useState<NewReminder>({
    title: '',
    description: '',
    type: 'once',
    reminder_date: new Date().toISOString().split('T')[0],
    reminder_time: '09:00',
    days_of_week: []
  });
  
  // Hook de conquistas
  const { achievement, showAchievement, checkAchievementOnPoints, closeAchievement } = useAchievements();
  
  // Hook de badges
  const { badgeNotification, checkAndUnlockBadges, closeBadgeNotification, showPendingBadges } = useBadges();

  // Função para fechar badge e recarregar dados
  const handleCloseBadge = useCallback(() => {
    try {
      const b = badgeNotification?.badge;
      const criteriaType = b?.badge?.criteria?.type as string | undefined;
      const name = (b?.badge?.name || '').toString().toLowerCase();
      const looksLikeFirstMedication =
        criteriaType === 'first_treatment' ||
        /primeir/.test(name) && /(homeop|rem[eé]dio|medic|treat)/.test(name) ||
        /first/.test(name) && /(treat|medic|remedy)/.test(name);
      if (looksLikeFirstMedication) {
        window.dispatchEvent(new CustomEvent('tutorial:badge:first-medication:closed'))
      }
    } catch { /* ignore */ }
    closeBadgeNotification();
  }, [badgeNotification, closeBadgeNotification]);

  // Dispara evento global para que modais/listas possam se atualizar automaticamente
  const dispatchPointsUpdated = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent('magistral:points-updated', { detail: { memberId } }));
    } catch (error) {
      console.warn('⚠️ [MemberDashboard] Falha ao disparar evento global de pontos:', error);
    }
  }, [memberId]);

  // Função para abrir o quiz (verifica se já foi completado)
  const handleQuizClick = async () => {
    const { QuizSystem } = await import('../../utils/QuizSystem');
    const quizSystem = QuizSystem.getInstance();
    const status = await quizSystem.getQuizStatus(member.id);
    
    if (status.hasCompletedToday) {
      // Se já completou, abre o resumo
      setShowQuizSummaryModal(true);
    } else {
      // Se não completou, abre o quiz
      setShowQuizModal(true);
    }
  };

  // Sininho vermelho: mesma regra do dashboard principal
  // Exibe quando existe dose urgente (atrasada OU até 30 minutos antes)
  const isUrgentTime = useCallback((timeStr?: string): boolean => {
    if (!timeStr) return false;
    const [hh, mm] = timeStr.substring(0, 5).split(':').map((n) => Number(n));
    if (Number.isNaN(hh) || Number.isNaN(mm)) return false;
    const now = new Date();
    const scheduled = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm, 0, 0);
    const diffMinutes = Math.floor((scheduled.getTime() - now.getTime()) / 60000);
    // urgente se já passou (negativo) ou se falta 30 min ou menos
    return diffMinutes <= 30;
  }, []);

  const shouldShowUrgentBellForMedication = useCallback((med: any): boolean => {
    if (!med) return false;
    if (med.taken) return false; // já tomada
    if (med.status === 'completed') return false; // não sinalizar concluídas
    // Oculta para tratamentos que ainda não começaram
    try {
      const startObj = parseLocalDate(med.start_date);
      if (startObj && new Date() < startObj) return false;
    } catch { /* ignore */ }
    if (med.isPaused) return false; // em pausa
    const t = Array.isArray(med.times) && med.times.length > 0 ? med.times[0] : undefined;
    return isUrgentTime(t);
  }, [isUrgentTime]);

  // hasUrgentDoses será definido após a criação do estado 'member'
  const [showPointsHistoryModal, setShowPointsHistoryModal] = useState(false);
  const [editingMedication, setEditingMedication] = useState<any>(null);

  const [newEvent, setNewEvent] = useState<NewHealthEvent>({
    type: 'positive',
    description: '',
  });

  const [newMedicalRecord, setNewMedicalRecord] = useState<NewMedicalRecord>({
    type: 'illness',
    description: '',
    severity: 'leve',
  });

  const [newTreatment, setNewTreatment] = useState<NewTreatment>({
    medicationName: '',
    potencyNumber: '6',
    potencyType: 'CH',
    dosageType: 'globules',
    dosage: '5',
    startDate: getTodayBRT(), // Data atual em BRT como padrão
    times: ['08:00'],
    frequency: 'daily',
    customDays: [],
    weeklyDayIndex: null,
    usageDays: '30',
    pauseDays: '7',
    totalPeriod: '3',
    notes: '',
  });

  // Context de prescrição (persistente entre navegações, por membro)
  const {
    medications: prescriptionMedications,
    selectedIndex: selectedMedicationIndex,
    setMedications: setPrescriptionMedications,
    setSelectedIndex: setSelectedMedicationIndex,
    clearPrescription,
    hasCachedData,
    setCurrentMember
  } = usePrescriptionContext();

  // Estados locais para análise
  const [analyzingPrescription, setAnalyzingPrescription] = useState(false);
  const [prescriptionAnalysisError, setPrescriptionAnalysisError] = useState<string | null>(null);
  const [showPrescriptionUpgradeModal, setShowPrescriptionUpgradeModal] = useState(false);
  const [prescriptionFeatureUsage, setPrescriptionFeatureUsage] = useState({ used: 0, limit: 30 });
  
  // Avatares: fallback e normalização de extensão
  const [avatarBroken, setAvatarBroken] = useState(false);
  const getAvatarSrc = useCallback((avatar?: string) => {
    if (!avatar || typeof avatar !== 'string') return '';
    if (avatar.includes('/Imagens/')) {
      try {
        const after = avatar.split('/Imagens/')[1] || '';
        const fileWithQuery = after.split('?')[0];
        const decoded = decodeURIComponent(fileWithQuery);
        const normalized = decoded.replace(/\.(png|jpg|jpeg|webp)$/i, '.webp');
        return `/Imagens/${normalized}`;
      } catch {
        if (avatar.startsWith('/Imagens/')) {
          return avatar.replace(/\.(png|jpg|jpeg|webp)(\?.*)?$/i, '.webp$2');
        }
      }
    }
    return avatar;
  }, []);

  // getRecentPointEvents e auxiliares removidos (t11): evitamos sobrescrever healthEvents com eventos derivados de pontos

  const parseLocalDate = (dateString?: string | null): Date | null => {
    if (!dateString) return null;
    const parts = dateString.split('-').map((part: string) => Number(part));
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return null;
    }
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  };

  const formatDatePtBR = (date: Date | null) =>
    date ? date.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '--';

  const formatDateDisplay = (dateString?: string | null) => formatDatePtBR(parseLocalDate(dateString));

  // removido: _formatIsoDateDisplay (não utilizado)

  /**
   * Ajusta start_date para o próximo dia da semana escolhido (medicação semanal)
   * @param startYmd Data inicial no formato 'YYYY-MM-DD'
   * @param weeklyDayIndex Dia da semana desejado (0=Dom, 1=Seg, ..., 6=Sáb)
   * @returns Data ajustada no formato 'YYYY-MM-DD'
   */
  const adjustStartDateForWeekly = (startYmd: string, weeklyDayIndex: number): string => {
    const date = new Date(`${startYmd}T12:00:00-03:00`);
    const current = date.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
    const delta = (weeklyDayIndex - current + 7) % 7;
    
    if (delta === 0) {
      // Já coincide com o dia escolhido
      return startYmd;
    }
    
    // Avança para o próximo dia escolhido
    date.setDate(date.getDate() + delta);
    return date.toISOString().split('T')[0];
  };

  /**
   * Ajusta start_date para o próximo dia válido (medicação personalizada)
   * @param startYmd Data inicial no formato 'YYYY-MM-DD'
   * @param customDayNames Array de nomes dos dias (ex: ['Seg', 'Ter', 'Qua'])
   * @returns Data ajustada no formato 'YYYY-MM-DD'
   */
  const adjustStartDateForCustom = (startYmd: string, customDayNames: string[]): string => {
    const date = new Date(`${startYmd}T12:00:00-03:00`);
    const currentDayIndex = date.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
    
    // Converter nomes para índices
    const customDayIndices = customDayNames.map(name => weekDays.indexOf(name));
    
    // Verificar se hoje já é um dia válido
    if (customDayIndices.includes(currentDayIndex)) {
      return startYmd;
    }
    
    // Encontrar o próximo dia válido
    for (let i = 1; i <= 7; i++) {
      const nextDayIndex = (currentDayIndex + i) % 7;
      if (customDayIndices.includes(nextDayIndex)) {
        date.setDate(date.getDate() + i);
        return date.toISOString().split('T')[0];
      }
    }
    
    // Fallback (não deveria chegar aqui)
    return startYmd;
  };

  // Função para calcular status e progresso da medicação
  const isMedicationInPause = (med: any, _referenceDate: Date) => {
    const refDate = parseLocalDate(getTodayBRT());
    if (!refDate) return false;
    const startDate = parseLocalDate(med.start_date);
    if (!startDate) return false;
    if (refDate < startDate) return false;

    const endDate = parseLocalDate(med.end_date);
    if (endDate && refDate > endDate) return false;

    const totalPeriodMonths = med.total_period_months || 0;
    if (totalPeriodMonths > 0) {
      const plannedEndDate = new Date(startDate);
      plannedEndDate.setDate(plannedEndDate.getDate() + totalPeriodMonths * 30 - 1);
      if (refDate > plannedEndDate) return false;
    }

    // Lógica especial para modo custom (dias específicos da semana)
    const frequency = med.frequency || '';
    if (frequency.startsWith('custom;')) {
      const parts = frequency.split(';');
      if (parts[1]) {
        try {
          const customDayIndices = parts[1].split(',').map((idx: string) => parseInt(idx));
          const dayOfWeek = refDate.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
          
          // Está em pausa se o dia da semana NÃO está na lista
          return !customDayIndices.includes(dayOfWeek);
        } catch {
          // Se falhar o parse, usar lógica padrão
        }
      }
    }

    // Lógica padrão para outros modos (daily, alternate, weekly)
    const usageDays = med.usage_days || 0;
    const pauseDays = med.pause_days || 0;
    if (usageDays <= 0 || pauseDays <= 0) return false;

    const cycleLength = usageDays + pauseDays;
    if (cycleLength <= 0) return false;

    const daysElapsed = Math.floor((refDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const position = ((daysElapsed % cycleLength) + cycleLength) % cycleLength;
    return position >= usageDays;
  };

  const getNextUsageDate = (med: any, _referenceDate: Date) => {
    const startDate = parseLocalDate(med.start_date);
    if (!startDate) return null;
    const refDate = parseLocalDate(getTodayBRT());
    if (!refDate) return null;

    // Lógica especial para modo custom (dias específicos da semana)
    const frequency = med.frequency || '';
    if (frequency.startsWith('custom;')) {
      const parts = frequency.split(';');
      if (parts[1]) {
        try {
          const customDayIndices = parts[1].split(',').map((idx: string) => parseInt(idx));
          
          // Buscar o próximo dia válido (1-7 dias à frente)
          for (let i = 1; i <= 7; i++) {
            const nextDate = new Date(refDate);
            nextDate.setDate(refDate.getDate() + i);
            const nextDayOfWeek = nextDate.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
            
            if (customDayIndices.includes(nextDayOfWeek)) {
              return nextDate;
            }
          }
          
          return null; // Não deveria chegar aqui se customDays está válido
        } catch {
          // Se falhar o parse, usar lógica padrão
        }
      }
    }

    // Lógica padrão para outros modos (daily, alternate, weekly)
    const usageDays = med.usage_days || 0;
    const pauseDays = med.pause_days || 0;
    const cycleLength = usageDays + pauseDays;

    if (usageDays <= 0 || pauseDays <= 0 || cycleLength <= 0) {
      return null;
    }

    if (refDate < startDate) {
      return null;
    }

    const daysElapsed = Math.floor((refDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const cyclesCompleted = Math.floor(daysElapsed / cycleLength);
    const nextCycleStart = new Date(startDate);
    nextCycleStart.setDate(startDate.getDate() + (cyclesCompleted + 1) * cycleLength);
    return nextCycleStart;
  };

  const calculateMedicationStatus = (med: any) => {
    const today = new Date();
    const startDate = parseLocalDate(med.start_date);
    const endDate = med.end_date ? parseLocalDate(med.end_date) : null;

    if (!startDate) {
      return {
        status: 'active' as const,
        progress: 0,
        daysElapsed: 0,
        totalDays: 0,
        daysUntilStart: 0,
      };
    }

    const msPerDay = 1000 * 60 * 60 * 24;
    const rawDiff = Math.floor((today.getTime() - startDate.getTime()) / msPerDay);
    const baseTotalDays = endDate
      ? Math.max(Math.floor((endDate.getTime() - startDate.getTime()) / msPerDay), 0)
      : med.total_period_months
      ? Math.max(med.total_period_months * 30, 0)
      : 90;

    let status: 'active' | 'paused' | 'completed' | 'upcoming';
    const daysElapsed = rawDiff >= 0 ? rawDiff : 0;
    const daysUntilStart = rawDiff < 0 ? Math.abs(rawDiff) : 0;
    let progress = 0;

    if (rawDiff < 0) {
      status = 'upcoming';
    } else if (!med.is_active) {
      status = 'completed';
    } else if (endDate && today > endDate) {
      status = 'completed';
    } else if (isMedicationInPause(med, today)) {
      status = 'paused';
    } else {
      status = 'active';
    }

    if (status !== 'upcoming' && baseTotalDays > 0) {
      const cappedElapsed = Math.min(daysElapsed, baseTotalDays);
      progress = Math.min(Math.max(Math.round((cappedElapsed / baseTotalDays) * 100), 0), 100);
    }

    return { status, progress, daysElapsed, totalDays: baseTotalDays, daysUntilStart };
  };

  const [member, setMember] = useState<Member>({
    id: memberId || '',
    name: '',
    avatar: 'ri-user-line',
    streak: 0,
    points: 0,
    status: 'good',
    todayDoses: {
      taken: 0,
      total: 0,
    },
    medications: [],
    medicationsGrouped: [],
    healthEvents: [],
    medicalRecords: [],
    reminders: [],
  });

  // Sincronizar membro atual com o Context de prescrição (por membro)
  useEffect(() => {
    if (member.id) {
      setCurrentMember(member.id);
      // Log removido por segurança (não expor member.id)
    }
  }, [member.id, setCurrentMember]);

  // Bell no header da seção quando há qualquer dose urgente neste membro
  const hasUrgentDoses = useMemo(() => {
    try {
      return (member.medications || []).filter(shouldShowUrgentBellForMedication).length;
    } catch (error) {
      console.warn('⚠️ [MemberDashboard] Erro ao calcular doses urgentes:', error);
      return 0;
    }
  }, [member.medications, shouldShowUrgentBellForMedication]);

  // Carregar dados do membro do banco de dados
  useEffect(() => {
    const loadMemberData = async () => {
      logger.sensitive('Carregando dados do membro', { memberId });
      setLoading(true);
      
      try {
        if (!memberId) {
          console.error('❌ [MemberDashboard] ID do membro não fornecido');
          navigate('/dashboard');
          return;
        }

        const applyPointsAndLevel = async (pointsData: MemberPoints | null) => {
          setMemberPoints(pointsData);

          if (!pointsData) {
            setMemberLevelTitle(null);
            return;
          }

          const title = await pointsService.getLevelTitle(pointsData.totalPoints, 'member');
          if (title) {
            setMemberLevelTitle(title);
          } else {
            const fallbackTitle = await pointsService.getLevelTitleByLevel(pointsData.level ?? 1, 'member');
            setMemberLevelTitle(fallbackTitle);
          }
        };

        // Buscar todos os membros da família
        const familyMembers = await familyService.getUserFamilyMembers();
        logger.debug('Membros da família carregados', { count: familyMembers.length });
        
        // Encontrar o membro específico
        const currentMember = familyMembers.find(m => m.id === memberId);
        
        if (!currentMember) {
          console.error('❌ [MemberDashboard] Membro não encontrado');
          navigate('/dashboard');
          return;
        }

        logger.debug('Membro encontrado', { name: currentMember.name });

        // Buscar pontos do membro (agora com streaks atualizados)
        let points = await pointsService.getMemberPoints(memberId);
        logger.debug('Pontos do membro', { totalPoints: points?.totalPoints, streak: points?.currentTreatmentStreak });
        await applyPointsAndLevel(points);
        let lastTotalPoints = points?.totalPoints ?? 0;

        const initialBadges = await pointsService.getMemberBadges(memberId);
        const initialBadgeCount = initialBadges.length;

        // Buscar contagem de badges desbloqueados
        setBadgesCount(initialBadgeCount);
        logger.debug('Badges desbloqueados', { count: initialBadgeCount });

        // Buscar adesão aos tratamentos
        try {
          const adherence = await adherenceService.getMemberAdherence(memberId);
          setMemberAdherence(adherence);
          logger.debug('Adesão do membro', { percentage: adherence?.adherence_percentage });
        } catch (error) {
          console.error('⚠️ [MemberDashboard] Erro ao buscar adesão:', error);
        }
        
        // Verificar badges recentes e exibir notificações
        logger.debug('Verificando badges recentes...');
        const newlyUnlockedBadges = await checkAndUnlockBadges(memberId, setBadgesCount);
        if (newlyUnlockedBadges.length > 0) {
          logger.debug('Badges desbloqueados nesta sessão', { count: newlyUnlockedBadges.length });
        } else {
          const showedPendingBadge = await showPendingBadges(memberId);
          if (showedPendingBadge) {
            logger.debug('Exibindo badge pendente já desbloqueado anteriormente');
          }
        }

        // Executar regularização retroativa apenas na primeira visita após o deploy
        const retroFixKey = `badge_retrofix_done_${memberId}`;
        if (!localStorage.getItem(retroFixKey)) {
          const retroUnlockedBadges = await pointsService.checkAndUnlockBadges(memberId, true);
          if (retroUnlockedBadges.length > 0) {
            logger.debug('Badges retroativos regularizados', { count: retroUnlockedBadges.length });
            const badgesAfterRetro = await pointsService.getMemberBadges(memberId);
            setBadgesCount(badgesAfterRetro.length);
          }
          localStorage.setItem(retroFixKey, 'true');
        }

        // Recarregar pontos após triggers de badges/nível
        points = await pointsService.getMemberPoints(memberId);
        logger.debug('Pontos do membro após retroativos', { totalPoints: points?.totalPoints });
        await applyPointsAndLevel(points);

        if (points && points.totalPoints !== lastTotalPoints) {
          await checkAchievementOnPoints(memberId, lastTotalPoints, points.totalPoints, 'auto_sync');
          lastTotalPoints = points.totalPoints;
        }

        // Buscar medicações do banco
        const medications = await familyService.getMemberMedications(memberId);

        // Aderência unificada por medicação
        try {
          const map = await MedicationService.getMemberMedicationUnifiedAdherence(memberId);
          setAdherenceMap(map);
        } catch (e) {
          console.warn('⚠️ [MemberDashboard] Falha ao carregar aderência unificada por medicação:', e);
        }
        logger.debug('Medicações carregadas', { count: medications.length });

        // Buscar registros médicos (v2)
        const medicalRecords = await familyService.getMemberMedicalRecordsV2(memberId);
        logger.debug('Registros médicos (v2)', { count: medicalRecords.length });

        // Buscar eventos de saúde (view unificada - t12) com fallback para tabela antiga
        let healthEvents = await familyService.getMemberHealthJournal(memberId, { limit: 50 });
        if (!healthEvents || healthEvents.length === 0) {
          const legacy = await familyService.getMemberHealthEvents(memberId);
          if (legacy && legacy.length > 0) {
            healthEvents = legacy;
          }
        }
        logger.debug('Eventos de saúde', { count: healthEvents.length });

        // Buscar lembretes
        const reminders = await familyService.getMemberReminders(memberId);
        logger.debug('Lembretes', { count: reminders.length });

        // Formatar medicações para o formato do dashboard
        // IMPORTANTE: Criar um card para CADA horário da medicação
        const formattedMedications = await Promise.all(
          medications.flatMap(async (med) => {
            const { status, progress, daysElapsed, totalDays } = calculateMedicationStatus(med);
            const isPausedNow = isMedicationInPause(med, new Date());
            const resumeDate = isPausedNow ? getNextUsageDate(med, new Date()) : null;
            const times = med.times || [];
            
            // Se não tem horários, criar um card padrão
            if (times.length === 0) {
              return [{
                id: med.id,
                name: `${med.remedy_name} ${med.potency || ''}`,
                remedy_name: med.remedy_name,
                potency: med.potency,
                dosage_type: med.dosage_type,
                quantity: med.quantity,
                times: [],
                frequency: med.frequency,
                start_date: med.start_date,
                end_date: med.end_date,
                usage_days: med.usage_days || 30,
                pause_days: med.pause_days || 0,
                total_period_months: med.total_period_months || 3,
                notes: med.notes,
                status,
                progress,
                daysElapsed,
                totalDays,
                taken: false,
                isPaused: isPausedNow,
                resumeDate: resumeDate ? resumeDate.toISOString() : null
              }];
            }
            
            // Criar um card para CADA horário
            return Promise.all(times.map(async (timeSlot: string) => {
              // Verificar se já foi tomada hoje para ESTE horário específico
              const wasTaken = await familyService.checkMedicationTakenToday(med.id, timeSlot);
              const slotResumeDate = isPausedNow ? getNextUsageDate(med, new Date()) : null;
              
              return {
                id: `${med.id}_${timeSlot}`, // ID único por horário
                medicationId: med.id, // ID original da medicação
                name: `${med.remedy_name} ${med.potency || ''}`,
                remedy_name: med.remedy_name,
                potency: med.potency,
                dosage_type: med.dosage_type,
                quantity: med.quantity,
                times: [timeSlot], // Apenas este horário
                frequency: med.frequency,
                start_date: med.start_date,
                end_date: med.end_date,
                usage_days: med.usage_days || 30,
                pause_days: med.pause_days || 0,
                total_period_months: med.total_period_months || 3,
                notes: med.notes,
                status,
                progress,
                daysElapsed,
                totalDays,
                taken: wasTaken,
                isPaused: isPausedNow,
                resumeDate: slotResumeDate ? slotResumeDate.toISOString() : null
              };
            }));
          })
        ).then(results => results.flat());

        // Criar versão agrupada das medicações (um card por medicação, com todos os horários)
        // Para usar na seção "Histórico de Tratamentos"
        const medicationsGrouped = medications.map(med => {
          const { status, progress, daysElapsed, totalDays } = calculateMedicationStatus(med);
          const isPausedNow = isMedicationInPause(med, new Date());
          const resumeDate = isPausedNow ? getNextUsageDate(med, new Date()) : null;
          
          logger.debug(`Medicação ${med.remedy_name}`, { status, progress, daysElapsed, totalDays });
          
          return {
            id: med.id,
            name: `${med.remedy_name} ${med.potency || ''}`,
            remedy_name: med.remedy_name,
            potency: med.potency,
            dosage_type: med.dosage_type,
            quantity: med.quantity,
            times: med.times || [],
            frequency: med.frequency,
            start_date: med.start_date,
            end_date: med.end_date,
            usage_days: med.usage_days || 30,
            pause_days: med.pause_days || 0,
            total_period_months: med.total_period_months || 3,
            notes: med.notes,
            status,
            progress,
            daysElapsed,
            totalDays,
            isPaused: isPausedNow,
            resumeDate: resumeDate ? resumeDate.toISOString() : null
          };
        });

        // Normaliza rótulos para doses perdidas no dashboard
          const normalizeMissedDoseDescription = (desc?: string): string => {
    if (!desc) return desc as any;
    return desc.replace(/^Dose perdida(?:\s*(?:\/|ou)\s*muito fora do horário)?\b/i, 'Dose perdida ou muito fora do horário');
  };

        // Formatar eventos de saúde
        const formattedHealthEvents = healthEvents.map((event: any) => ({
          id: event.id,
          type: event.event_type as 'positive' | 'negative',
          description: normalizeMissedDoseDescription(event.description),
          date: new Date(event.created_at).toLocaleDateString('pt-BR'),
          category: event.category || 'general',
          icon: undefined as unknown as string
        }));

        // Formatar lembretes
        const formattedReminders = reminders.map(reminder => ({
          id: reminder.id,
          title: reminder.title,
          description: reminder.description,
          reminder_date: reminder.reminder_date,
          reminder_time: reminder.reminder_time,
          days_of_week: reminder.days_of_week,
          created_at: reminder.created_at
        }));

        // Atualizar estado com dados reais do banco
        setMember({
          id: currentMember.id || '',
          name: currentMember.name,
          avatar: currentMember.avatar_url || 'ri-user-line',
          streak: points?.currentTreatmentStreak ?? 0,
          points: points?.totalPoints ?? 0,
          status: 'good', // TODO: Calcular baseado em aderência
          todayDoses: {
            taken: 0, // TODO: Calcular medicações tomadas hoje
            total: formattedMedications.filter(m => m.status !== 'completed' && !m.isPaused).length,
          },
          medications: formattedMedications.filter(m => m.status !== 'completed'),
          medicationsGrouped: medicationsGrouped,
          healthEvents: formattedHealthEvents,
          medicalRecords: medicalRecords,
          reminders: formattedReminders,
        });
      } catch (error) {
        console.error('❌ [MemberDashboard] Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMemberData();
  }, [memberId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persistir último membro visitado para retomada do tutorial (passos 6.x)
  useEffect(() => {
    if (memberId) {
      try {
        const uid = user?.id || ''
        const key = uid ? `tutorial:${uid}:lastMemberId` : 'tutorial:lastMemberId'
        localStorage.setItem(key, memberId)
      } catch { /* ignore */ }
    }
  }, [memberId, user?.id])

  // Auto abrir resumo de tratamento concluído (uma vez por visita)
  useEffect(() => {
    const tryOpen = async () => {
      try {
        if (!member.id) return;
        if (showQuizModal || showQuizSummaryModal || showBadgeGallery || showHealthHistoryModal || showAddEventModal || showAddReminderModal || showTreatmentModal || badgeNotification?.show || showAchievement) return;
        if (autoSummaryOpened) return;
        const unseen = await TreatmentSummaryService.getUnseenCompleted(member.id);
        if (unseen && unseen.length > 0) {
          const med = unseen[0];
          setSummaryMedicationId(med.id);
          setShowTreatmentSummaryModal(true);
          setAutoSummaryOpened(true);
        }
      } catch (e) { console.warn('Failed to auto-open treatment summary', e); }
    };
    void tryOpen();
  }, [member.id, showQuizModal, showQuizSummaryModal, showBadgeGallery, showHealthHistoryModal, showAddEventModal, showAddReminderModal, showTreatmentModal, badgeNotification?.show, showAchievement, autoSummaryOpened]);

  // Ao trocar de membro, permitir uma nova abertura automática por visita para o novo membro
  useEffect(() => {
    setAutoSummaryOpened(false);
  }, [member.id]);

  // Ouvir comando do TutorialProvider para garantir que o modal de medicação esteja aberto ao retomar no meio dos passos 6.x
  useEffect(() => {
    const onEnsureModal = () => {
      try {
        setShowOptionsMenu(false);
        setEditingMedication(null);
        setShowTreatmentModal(true);
      } catch { /* ignore */ }
    }
    window.addEventListener('tutorial:ensure-medication-modal-open', onEnsureModal)
    return () => window.removeEventListener('tutorial:ensure-medication-modal-open', onEnsureModal)
  }, [])

  // Flag de cadastro de medicação incompleto: quando modal abre, marcar; ao salvar/cancelar, limpar
  useEffect(() => {
    try {
      const uid = user?.id || ''
      const key = uid ? `tutorial:${uid}:med_incomplete` : 'tutorial:med_incomplete'
      if (showTreatmentModal) {
        localStorage.setItem(key, '1')
      } else {
        localStorage.removeItem(key)
      }
    } catch { /* ignore */ }
  }, [showTreatmentModal, user?.id])

  // Realtime consolidado: único canal com múltiplos handlers (pontos, lembretes e notification_queue)
  useEffect(() => {
    if (!memberId) return;

    const channelName = `md_${user?.id || 'anon'}_${memberId}`;
    if (import.meta.env.DEV) {
      try { console.debug('[MemberDashboard] Subscribing consolidated channel', { channelName }); } catch {
        // Silencioso - apenas debug
      }
    }

    // Debounce local para coalescer recargas de lembretes
    let remindersTimer: number | null = null;
    const scheduleRemindersRefresh = () => {
      if (remindersTimer) {
        window.clearTimeout(remindersTimer);
      }
      remindersTimer = window.setTimeout(async () => {
        try {
          const reminders = await familyService.getMemberReminders(memberId);
          const formattedReminders = reminders.map((reminder: any) => ({
            id: reminder.id,
            title: reminder.title,
            description: reminder.description,
            reminder_date: reminder.reminder_date,
            reminder_time: reminder.reminder_time,
            days_of_week: reminder.days_of_week,
            created_at: reminder.created_at,
          }));
          setMember(prev => ({ ...prev, reminders: formattedReminders }));
        } catch (error) {
          console.warn('⚠️ [Realtime] Falha ao atualizar lembretes (debounced):', error);
        }
      }, 400);
    };

    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'member_points_v2' as any,
        filter: `member_id=eq.${memberId}`
      }, async () => {
        try {
          const p = await pointsService.getMemberPoints(memberId);
          if (p) {
            setMemberPoints(p);
            setMember(prev => ({
              ...prev,
              streak: p.currentTreatmentStreak ?? prev.streak,
              points: p.totalPoints ?? prev.points
            }));
            dispatchPointsUpdated();
          }
        } catch (error) {
          console.warn('⚠️ [Realtime] Falha ao atualizar pontos após evento:', error);
        }
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'reminders' as any,
        filter: `member_id=eq.${memberId}`
      }, () => {
        scheduleRemindersRefresh();
      });

    if (user?.id) {
      channel.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'notification_queue' as any,
        filter: `user_id=eq.${user.id}`
      }, (payload: any) => {
        try {
          const row = payload?.new || payload?.old;
          const data = row?.data || {};
          const reminderId = data?.reminder_id;
          const mode = data?.mode;
          if (reminderId && mode !== 'recurring') {
            scheduleRemindersRefresh();
          }
        } catch (error) {
          console.warn('⚠️ [Realtime] Falha ao reagir a mudanças na notification_queue:', error);
        }
      });
    }

    channel.subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
        if (import.meta.env.DEV) {
          try { console.debug('[MemberDashboard] Unsubscribed consolidated channel', { channelName }); } catch {
            // Silencioso - apenas debug
          }
        }
      } catch (error) {
        console.warn('⚠️ [MemberDashboard] Falha ao remover canal realtime (consolidado):', error);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, user?.id]);

  // Função para obter cor do ícone baseada na categoria
  const getCategoryIconColor = (category: string, type: 'positive' | 'negative', description?: string) => {
    if (type === 'negative') {
      return 'bg-red-100 text-red-500';
    }
    
    switch (category) {
      case 'treatment':
        // Verde para dose tomada no horário; amarelo para outras positivas (ex.: fora do horário)
        if (description && description.toLowerCase().startsWith('dose tomada no horário')) {
          return 'bg-emerald-100 text-emerald-600';
        }
        return 'bg-orange-100 text-orange-500';
      case 'illness':
        return 'bg-emerald-100 text-emerald-500';
      case 'allopathic':
        return 'bg-blue-100 text-blue-500';
      case 'medical_record':
        return 'bg-red-100 text-red-500';
      case 'badge':
        return 'bg-yellow-100 text-yellow-600';
      case 'level':
        return 'bg-blue-100 text-blue-600';
      default:
        return 'bg-emerald-100 text-emerald-500';
    }
  };

  // Função para obter ícone baseado na categoria
  const getCategoryIcon = (category: string, type: 'positive' | 'negative') => {
    if (type === 'negative') {
      return 'ri-alert-line';
    }
    
    switch (category) {
      case 'treatment':
        return 'ri-medicine-bottle-line';
      case 'illness':
        return 'ri-shield-check-line';
      case 'allopathic':
        return 'ri-medicine-bottle-fill';
      case 'medical_record':
        return 'ri-health-book-line';
      case 'badge':
        return 'ri-award-fill';
      case 'level':
        return 'ri-arrow-up-circle-fill';
      default:
        return 'ri-heart-fill';
    }
  };

  const markDoseAsTaken = async (cardId: string) => {
    if (!user || !memberId) return;

    logger.debug('Marcando dose como tomada', { cardId });

    // Salvar pontos atuais ANTES de aplicar a regra
    const oldPoints = memberPoints?.totalPoints || 0;

    // Encontrar o card da medicação (pode ter _timeSlot no ID)
    const medication = member.medications.find(m => m.id === cardId);
    if (!medication) {
      console.error('❌ [Medication] Medicação não encontrada');
      return;
    }

    // Bloqueio: não permitir registro em tratamento concluído
    if (medication.status === 'completed') {
      toast.info('Tratamento concluído — não é possível registrar doses.');
      return;
    }

    // Extrair o ID real da medicação (sem o sufixo _timeSlot)
    const realMedicationId = medication.medicationId || medication.id.split('_')[0];

    // Determinar se está no horário ou atrasado
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    // Verificar se algum horário programado está próximo (dentro de 4h)
    let isOnTime = false;
    let isDelayed = false;
    
    for (const scheduledTime of medication.times) {
      const [schedHour, schedMin] = scheduledTime.split(':').map(Number);
      const scheduledDate = new Date();
      scheduledDate.setHours(schedHour, schedMin, 0, 0);
      
      const diffMinutes = (now.getTime() - scheduledDate.getTime()) / (1000 * 60);
      
      if (Math.abs(diffMinutes) <= 30) {
        isOnTime = true;
        break;
      } else if (diffMinutes > 30 && diffMinutes <= 240) {
        isDelayed = true;
      }
    }

    logger.debug('Horário da dose', { currentTime, isOnTime, isDelayed });

    try {
      // ✅ MIGRAÇÃO V2: Backend calcula pontos automaticamente via register_dose_v2()
      // Não aplicar pontos manualmente aqui - evita duplicação
      
      // Preparar dados para salvar no banco
      // CORRIGIDO (18/10/2025 21:27): Usar data LOCAL (BRT) para evitar bug após 21:00
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const today = `${year}-${month}-${day}`; // Data local (BRT)
      
      const time = medication.times[0] || '08:00';
      
      logger.debug('Horário e data', { time, today });
      
      // Assegura que o formato seja sempre HH:mm:ss
      const timeParts = time.split(':');
      let formattedTime;
      if (timeParts.length === 2) {
        // Formato HH:mm - adicionar segundos
        formattedTime = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}:00`;
      } else if (timeParts.length === 3) {
        // Formato HH:mm:ss - usar como está
        formattedTime = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}:${timeParts[2].padStart(2, '0')}`;
      } else {
        // Fallback
        formattedTime = '08:00:00';
      }
      
      // Criar data/hora em BRT e converter para UTC
      // Exemplo: 18/10/2025 21:20 BRT → 19/10/2025 00:20 UTC
      const localDateTime = `${today}T${formattedTime}-03:00`; // BRT = UTC-3
      const scheduledDateTime = new Date(localDateTime).toISOString();
      logger.debug('Data/hora BRT→UTC', { scheduledDateTime });
      
      // Salvar no banco - register_dose_v2() calcula pontos automaticamente
      const statusToRegister: 'taken' | 'late' = isOnTime ? 'taken' : 'late';
      const takenAt = new Date().toISOString();

      const result = await familyService.logMedicationTaken(
        realMedicationId,
        scheduledDateTime,
        statusToRegister,
        takenAt
      );
      logger.debug('Log salvo no banco', { result });
      
      // Atualizar UI
      setMember((prev) => {
        const updatedMedications = prev.medications.map((med) =>
          med.id === cardId ? { ...med, taken: true } : med,
        );

        const newTakenCount = updatedMedications.filter((med) => med.taken).length;

        return {
          ...prev,
          medications: updatedMedications,
          todayDoses: {
            ...prev.todayDoses,
            taken: newTakenCount,
          },
        };
      });

      // Atualizar eventos com a nova pontuação
      setTimeout(async () => {
        // (t11) Removido: não sobrescrever healthEvents com eventos derivados de pontos
        
        // Buscar pontos atualizados e verificar conquistas
        let updatedPoints = await pointsService.getMemberPoints(memberId);
        setMemberPoints(updatedPoints);
        dispatchPointsUpdated();

        // Fallback: aguardar consistência eventual das triggers e tentar novamente
        if (!updatedPoints || updatedPoints.totalPoints === oldPoints) {
          await new Promise((r) => setTimeout(r, 350));
          const retryPoints = await pointsService.getMemberPoints(memberId);
          if (retryPoints) {
            updatedPoints = retryPoints;
            setMemberPoints(retryPoints);
            dispatchPointsUpdated();
          }
        }
        
        if (updatedPoints) {
          // Verificar se houve conquista
          await checkAchievementOnPoints(
            memberId,
            oldPoints,
            updatedPoints.totalPoints,
            'medication_taken'
          );
          
          // Verificar e desbloquear badges automaticamente
          const pointsBeforeBadges = updatedPoints.totalPoints;
          await checkAndUnlockBadges(memberId, setBadgesCount);

          // Refetch pontos após possíveis badges (ex.: Primeiras Gotas)
          let finalPoints: MemberPoints | null = updatedPoints;
          try {
            let tries = 0;
            let afterBadges: MemberPoints | null = null;
            while (tries < 3) {
              await new Promise(r => setTimeout(r, 250));
              afterBadges = await pointsService.getMemberPoints(memberId);
              if (afterBadges && afterBadges.totalPoints > pointsBeforeBadges) break;
              tries++;
            }
            if (afterBadges && afterBadges.totalPoints !== pointsBeforeBadges) {
              finalPoints = afterBadges;
              setMemberPoints(afterBadges);
              dispatchPointsUpdated();
            }
          } catch (err) {
            console.warn('⚠️ [Medication] Falha no refetch pós-badges:', err);
          }
          
          if (finalPoints) {
            // Atualizar título se mudou de nível
            const newTitle = await pointsService.getLevelTitle(finalPoints.totalPoints, 'member');
            if (newTitle) {
              setMemberLevelTitle(newTitle);
            } else {
              const fallbackTitle = await pointsService.getLevelTitleByLevel(finalPoints.level ?? 1, 'member');
              setMemberLevelTitle(fallbackTitle);
            }

            setMember(prev => ({
              ...prev,
              streak: finalPoints.currentTreatmentStreak ?? prev.streak,
              points: finalPoints.totalPoints ?? prev.points
            }));
            dispatchPointsUpdated();
          }
        }
      }, 100);

      logger.success('Dose marcada com sucesso');
    } catch (error) {
      console.error('❌ [Medication] Erro ao processar medicação:', error);
    }
  };

  const handleAddHealthEvent = async () => {
    if (!newEvent.description.trim() || !user || !memberId) return;

    logger.debug('Adicionando evento de saúde', { type: newEvent.type });

    try {
      // Salvar no banco de dados
      await familyService.addHealthEvent(member.id, {
        event_type: newEvent.type,
        category: 'general',
        description: newEvent.description,
        severity: newEvent.type === 'negative' ? 'medium' : null,
        event_date: new Date().toISOString().split('T')[0]
      });

      // v2: Não aplicar pontos/streaks via frontend para eventos gerais.
      // Para alterações de pontos/streaks, use registerMedicalRecordV2() (ver handleAddMedicalRecord)

      // Recarregar eventos do banco
      const healthEvents = await familyService.getMemberHealthEvents(member.id);
      const formattedHealthEvents = healthEvents.map((event: any) => ({
        id: event.id,
        type: event.event_type as 'positive' | 'negative',
        description: event.description,
        date: new Date(event.created_at).toLocaleDateString('pt-BR'),
        category: event.category || 'general',
        icon: event.event_type === 'positive' ? 'ri-heart-fill' : 'ri-alert-line'
      }));

      setMember((prev) => ({
        ...prev,
        healthEvents: formattedHealthEvents
      }));

      setNewEvent({ type: 'positive', description: '' });
      setShowAddEventModal(false);
      
      logger.success('Evento de saúde registrado');
    } catch (error) {
      console.error('❌ [HealthEvent] Erro ao adicionar evento de saúde:', error);
      toast.error('Erro ao salvar evento de saúde. Tente novamente.');
    }
  };

  const handleAddMedicalRecord = async () => {
    if (!newMedicalRecord.description.trim() || !user || !memberId) return;

    logger.debug('Adicionando registro médico', { type: newMedicalRecord.type });

    try {
      // ✅ PARTE 4: Usar função do banco register_medical_record_v2()
      // Esta função faz TUDO automaticamente:
      // 1. Cria registro em medical_records_v2
      // 2. Cria evento em health_events
      // 3. Trigger quebra o streak automaticamente
      // 4. Pontos são aplicados via trigger
      
      const event = newMedicalRecord.type === 'illness' ? 'doenca' : 'medicamento_alopatico';
      
      await familyService.registerMedicalRecordV2(
        member.id,
        event,
        newMedicalRecord.severity,
        newMedicalRecord.medicationType,
        newMedicalRecord.description
      );

      logger.success('Registro médico criado');

      // Recarregar registros médicos (v2) e eventos de saúde do banco
      const medicalRecords = await familyService.getMemberMedicalRecordsV2(member.id);
      const healthEvents = await familyService.getMemberHealthEvents(member.id);
      const formattedHealthEvents = healthEvents.map((event: any) => ({
        id: event.id,
        type: event.event_type as 'positive' | 'negative',
        description: event.description,
        date: new Date(event.created_at).toLocaleDateString('pt-BR'),
        category: event.category || 'general',
        icon: event.event_type === 'positive' ? 'ri-heart-fill' : 'ri-alert-line'
      }));
      
      // IMPORTANTE: Recarregar pontos para atualizar os streaks na UI
      const updatedPoints = await pointsService.getMemberPoints(memberId);
      setMemberPoints(updatedPoints);
      logger.sensitive('Pontos recarregados após registro médico', { memberId: updatedPoints?.memberId, totalPoints: updatedPoints?.totalPoints });
      
      setMember((prev) => ({
        ...prev,
        medicalRecords: medicalRecords,
        healthEvents: formattedHealthEvents
      }));

      setNewMedicalRecord({ type: 'illness', description: '', severity: 'leve' });
      setShowAddMedicalModal(false);
      
      console.log('✅ [MedicalRecord] Registro médico salvo com sucesso!');
    } catch (error) {
      console.error('❌ [MedicalRecord] Erro ao adicionar registro médico:', error);
      toast.error('Erro ao salvar registro médico. Tente novamente.');
    }
  };

  // Helper: agenda push notifications para lembretes
  const scheduleReminderPushes = async (saved: any) => {
    try {
      if (!user?.id) return;
      const timeStr = (saved.reminder_time || '09:00:00').substring(0, 5);
      const title = `Lembrete: ${saved.title || 'Evento'}`;

      const toISOAtBRT = (dateStr: string, hhmm: string) => {
        // Monta Date com offset BRT fixo (-03:00)
        const iso = new Date(`${dateStr}T${hhmm}:00-03:00`).toISOString();
        return iso;
      };

      const enqueue = async (dateStr: string) => {
        const scheduledISO = toISOAtBRT(dateStr, timeStr);
        const when = new Date(scheduledISO);
        if (when.getTime() <= Date.now()) return; // não enfileirar passado
        const br = formatDateBR(dateStr);
        const body = `${br} às ${timeStr}`;
        await pushService.enqueue(user.id, title, body, {
          data: {
            type: `reminder_${saved.id}`,
            reminder_id: saved.id,
            member_id: member.id,
            member_name: member.name,
            time: timeStr,
            title: saved.title || 'Lembrete',
          },
          scheduledAt: when,
        });
      };

      if (saved.reminder_date) {
        await enqueue(saved.reminder_date);
      } else if (Array.isArray(saved.days_of_week)) {
        const today = new Date();
        for (let i = 0; i < 7; i++) {
          const d = new Date(today);
          d.setDate(today.getDate() + i);
          const dow = d.getDay();
          if (saved.days_of_week.includes(dow)) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const dateStr = `${y}-${m}-${day}`;
            await enqueue(dateStr);
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ Falha ao agendar push do lembrete (não bloqueante):', error);
    }
  };

  // Salvar (criar/atualizar) lembrete
  const handleSaveReminder = async () => {
    if (!newReminder.title.trim()) return;
    try {
      const payload: any = {
        title: newReminder.title,
        description: newReminder.description || null,
        reminder_time: `${newReminder.reminder_time}:00`,
        is_active: true,
      };

      if (newReminder.type === 'once') {
        payload.reminder_date = newReminder.reminder_date;
        payload.days_of_week = null;
      } else {
        payload.reminder_date = null;
        payload.days_of_week = newReminder.days_of_week;
      }

      let saved: any;
      if (editingReminder) {
        // Limpar filas futuras deste lembrete (evitar push em horários antigos)
        try {
          if (user?.id) {
            const nowIso = new Date().toISOString();
            await supabase
              .from('notification_queue' as any)
              .delete()
              .eq('user_id', user.id)
              .eq('status', 'pending')
              .gte('scheduled_at', nowIso)
              .contains('data' as any, { reminder_id: editingReminder.id });
          }
        } catch (error) {
          console.warn('⚠️ Falha ao limpar filas antigas do lembrete (não bloqueante):', error);
        }
        saved = await familyService.updateReminder(editingReminder.id, payload);
      } else {
        saved = await familyService.addReminder(member.id, payload);
      }

      // Agendar push para o lembrete salvo
      await scheduleReminderPushes(saved);

      // Recarregar lembretes
      const reminders = await familyService.getMemberReminders(member.id);
      const formattedReminders = reminders.map((reminder) => ({
        id: reminder.id,
        title: reminder.title,
        description: reminder.description,
        reminder_date: reminder.reminder_date,
        reminder_time: reminder.reminder_time,
        days_of_week: reminder.days_of_week,
        created_at: reminder.created_at,
      }));

      setMember((prev) => ({ ...prev, reminders: formattedReminders }));

      // Reset
      setNewReminder({
        title: '',
        description: '',
        type: 'once',
        reminder_date: new Date().toISOString().split('T')[0],
        reminder_time: '09:00',
        days_of_week: [],
      });
      setEditingReminder(null);
      setShowAddReminderModal(false);
      toast.success('Lembrete salvo com sucesso');
    } catch (error) {
      console.error('❌ Erro ao salvar lembrete:', error);
      toast.error('Erro ao salvar lembrete. Tente novamente.');
    }
  };

  

  const getSeverityColor = (severity?: 'leve' | 'moderado' | 'alto') => {
    switch (severity) {
      case 'alto':
        return 'bg-vermelho-100 text-vermelho-600';
      case 'moderado':
        return 'bg-laranja-100 text-laranja-600';
      case 'leve':
      default:
        return 'bg-verde-100 text-verde-600';
    }
  };

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  // Formata YYYY-MM-DD em DD/MM/YYYY sem envolver objeto Date (evita deslocamento UTC)
  const formatDateBR = (dateStr?: string) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const [y, m, d] = parts;
    return `${d}/${m}/${y}`;
  };

  const addTimeSlot = () => {
    if (newTreatment.times.length < 6) {
      setNewTreatment((prev) => ({
        ...prev,
        times: [...prev.times, '12:00'],
      }));
    }
  };

  const removeTimeSlot = (index: number) => {
    if (newTreatment.times.length > 1) {
      setNewTreatment((prev) => ({
        ...prev,
        times: prev.times.filter((_, i) => i !== index),
      }));
    }
  };

  const updateTimeSlot = (index: number, time: string) => {
    setNewTreatment((prev) => ({
      ...prev,
      times: prev.times.map((t, i) => (i === index ? time : t)),
    }));
    try {
      if (time && time.length >= 4) {
        window.dispatchEvent(new CustomEvent('tutorial:med:schedule-filled'))
      }
    } catch { /* ignore */ }
  };

  const toggleCustomDay = (day: string) => {
    setNewTreatment((prev) => {
      const newCustomDays = prev.customDays.includes(day)
        ? prev.customDays.filter((d) => d !== day)
        : [...prev.customDays, day];
      
      // Calcular automaticamente uso/pausa baseado nos dias selecionados
      const usageDays = newCustomDays.length;
      const pauseDays = 7 - usageDays;
      
      return {
        ...prev,
        customDays: newCustomDays,
        usageDays: usageDays.toString(),
        pauseDays: pauseDays.toString(),
      };
    });
  };

  const handleEditMedication = (medication: any) => {
    // Extrair número e tipo da potência (ex: "30CH" -> número: "30", tipo: "CH")
  const potencyMatch = medication.potency?.match(/^(\d+)([A-Z]+)$/);
    const potencyNumber = potencyMatch ? potencyMatch[1] : '6';
    const potencyType = potencyMatch ? potencyMatch[2] : 'CH';

    // Parsear frequency para weekly e custom
    let frequency = medication.frequency || 'daily';
    let weeklyDayIndex = null;
    let customDays: string[] = [];
    
    if (frequency.startsWith('weekly;')) {
      // Formato novo: 'weekly;N'
      const parts = frequency.split(';');
      weeklyDayIndex = parseInt(parts[1]);
      frequency = 'weekly';
    } else if (frequency === 'weekly') {
      // Formato antigo: inferir dia do start_date
      const startDate = new Date(`${medication.start_date}T12:00:00-03:00`);
      weeklyDayIndex = startDate.getDay();
    } else if (frequency.startsWith('custom;')) {
      // Formato novo: 'custom;0,2,4' (índices dos dias)
      const parts = frequency.split(';');
      if (parts[1]) {
        const dayIndices = parts[1].split(',').map((idx: string) => parseInt(idx));
        customDays = dayIndices.map((idx: number) => weekDays[idx]);
      }
      frequency = 'custom';
    }

    // Preencher o formulário com os dados da medicação
    setNewTreatment({
      medicationName: medication.remedy_name,
      potencyNumber: potencyNumber,
      potencyType: potencyType,
      dosageType: medication.dosage_type,
      dosage: medication.quantity.toString(),
      startDate: medication.start_date,
      times: medication.times || ['08:00'],
      frequency: frequency,
      customDays: customDays,
      weeklyDayIndex: weeklyDayIndex,
      usageDays: medication.usage_days.toString(),
      pauseDays: medication.pause_days.toString(),
      totalPeriod: medication.total_period_months.toString(),
      notes: medication.notes || '',
    });

    setEditingMedication(medication);
    setShowTreatmentModal(true);
  };

  // Helper: Converter arquivo para base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remover prefixo "data:...;base64,"
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Helper: Aplicar medicamento selecionado aos campos
  const applySelectedMedication = (index: number) => {
    if (index < 0 || index >= prescriptionMedications.length) return;
    
    const med = prescriptionMedications[index];
    
    setNewTreatment(prev => ({
      ...prev,
      medicationName: med.remedy_name || '',
      potencyNumber: med.potency_number || '6',
      potencyType: med.potency_type || 'CH',
      dosageType: med.dosage_type || 'globules',
      dosage: med.quantity || '5',
      times: med.times && med.times.length > 0 ? med.times : ['08:00'],
      frequency: med.frequency || 'daily',
      customDays: med.custom_days || [],
      weeklyDayIndex: med.weekly_day_index ?? null,
      usageDays: med.usage_days || '30',
      pauseDays: med.pause_days || '7',
      totalPeriod: med.total_period || '3',
      notes: med.notes || ''
    }));
    
    setSelectedMedicationIndex(index);
  };

  // Handler: Upload e análise de prescrição
  const handlePrescriptionUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ✅ VERIFICAR FEATURE GATE (quota de análise de prescrição)
    if (user) {
      const access = await featureGateService.canUseFeature(user.id, 'prescription_analysis');
      
      if (!access.allowed) {
        // Limite atingido - mostrar modal de upgrade
        setPrescriptionFeatureUsage({ 
          used: access.used || 0, 
          limit: access.limit || 30 
        });
        setShowPrescriptionUpgradeModal(true);
        e.target.value = ''; // Limpar input
        return;
      }
    }

    // 🚫 BLOQUEAR PDFs (custo 100x maior que imagens)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      toast.error('Apenas fotos são aceitas (JPEG/PNG). PDFs não são suportados devido ao custo excessivo de processamento.');
      e.target.value = ''; // Limpar input
      return;
    }

    // Validar tamanho (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 10MB');
      e.target.value = ''; // Limpar input
      return;
    }

    setAnalyzingPrescription(true);
    setPrescriptionAnalysisError(null);
    setPrescriptionMedications([], member.id);
    setSelectedMedicationIndex(null);

    try {
      console.log('📄 [Prescription] Convertendo arquivo para base64...');
      const base64 = await fileToBase64(file);
      
      console.log('🤖 [Prescription] Chamando Edge Function...');
      const response = await supabase.functions.invoke('prescription-ai-analyzer', {
        body: {
          file_content: base64,
          file_type: file.type,
          file_name: file.name
        }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Falha ao analisar prescrição');
      }

      const result = response.data;

      if (!result.success) {
        throw new Error(result.error || 'Erro desconhecido');
      }

      console.log('✅ [Prescription] Análise concluída:', result);

      const medications = result.medications || [];

      if (medications.length === 0) {
        throw new Error('Nenhum medicamento encontrado na prescrição');
      }

      // ✅ Registrar uso da feature (incrementar contador)
      if (user) {
        await featureGateService.consumeFeature(user.id, 'prescription_analysis');
        console.log('[Prescription] Feature consumida com sucesso');
      }

      setPrescriptionMedications(medications, member.id);

      // Se houver apenas 1 medicamento, aplicar automaticamente
      if (medications.length === 1) {
        applySelectedMedication(0);
        toast.success('Receita analisada! 1 medicamento encontrado.');
      } else {
        toast.success(
          `Receita analisada! ${medications.length} medicamentos encontrados. Selecione um abaixo.`
        );
      }

    } catch (error) {
      console.error('❌ [Prescription] Erro:', error);
      setPrescriptionAnalysisError(
        error instanceof Error ? error.message : 'Erro ao processar arquivo'
      );
      toast.error('Não foi possível analisar a receita. Tente novamente.');
    } finally {
      setAnalyzingPrescription(false);
      // Limpar input para permitir reenvio
      e.target.value = '';
    }
  };

  const handleSaveTreatment = async () => {
    if (newTreatment.medicationName && newTreatment.potencyNumber && newTreatment.potencyType && newTreatment.startDate) {
      // Validação específica para semanal
      if (newTreatment.frequency === 'weekly' && newTreatment.weeklyDayIndex === null) {
        toast.error('Selecione o dia da semana para medicação semanal');
        return;
      }
      
      // Validação específica para personalizado
      if (newTreatment.frequency === 'custom' && newTreatment.customDays.length === 0) {
        toast.error('Selecione pelo menos um dia da semana para medicação personalizada');
        return;
      }

      setSavingTreatment(true);
      try {
        const isEditing = editingMedication !== null;
        console.log(`💊 [MemberDashboard] ${isEditing ? 'Atualizando' : 'Salvando'} tratamento:`, newTreatment);

        // Preparar frequency, start_date, usage_days e pause_days
        let frequencyToSave: string = newTreatment.frequency;
        let startDateToSave = newTreatment.startDate;
        let usageDaysToSave = parseInt(newTreatment.usageDays);
        let pauseDaysToSave = parseInt(newTreatment.pauseDays);
        
        if (newTreatment.frequency === 'weekly') {
          // Encoding: 'weekly;N'
          frequencyToSave = `weekly;${newTreatment.weeklyDayIndex}`;
          
          // Ajustar start_date para o dia da semana escolhido
          startDateToSave = adjustStartDateForWeekly(
            newTreatment.startDate, 
            newTreatment.weeklyDayIndex!
          );
          
          // Forçar 1 dia de uso + 6 de pausa
          usageDaysToSave = 1;
          pauseDaysToSave = 6;
          
          // Notificar usuário se a data mudou
          if (startDateToSave !== newTreatment.startDate) {
            toast.info(
              `Data de início ajustada para ${formatDateDisplay(startDateToSave)} ` +
              `(próximo ${weekDays[newTreatment.weeklyDayIndex!]})`
            );
          }
        } else if (newTreatment.frequency === 'custom') {
          // Encoding: 'custom;0,2,4' (índices dos dias selecionados)
          const customDayIndices = newTreatment.customDays
            .map(dayName => weekDays.indexOf(dayName))
            .sort((a, b) => a - b);
          
          frequencyToSave = `custom;${customDayIndices.join(',')}`;
          
          // Ajustar start_date para o próximo dia válido
          startDateToSave = adjustStartDateForCustom(
            newTreatment.startDate,
            newTreatment.customDays
          );
          
          // Forçar dias de uso = quantidade de dias selecionados
          usageDaysToSave = newTreatment.customDays.length;
          pauseDaysToSave = 7 - usageDaysToSave;
          
          // Notificar usuário se a data mudou
          if (startDateToSave !== newTreatment.startDate) {
            const daysList = newTreatment.customDays.join(', ');
            toast.info(
              `Data de início ajustada para ${formatDateDisplay(startDateToSave)} ` +
              `(próximo dia válido: ${daysList})`
            );
          }
        } else if (newTreatment.frequency === 'alternate') {
          // Dias Alternados: 1 dia de uso + 1 dia de pausa
          usageDaysToSave = 1;
          pauseDaysToSave = 1;
        }

        // Calcular data de término
        const startDate = new Date(startDateToSave);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + parseInt(newTreatment.totalPeriod));

        const medicationData = {
          remedy_name: newTreatment.medicationName,
          potency: `${newTreatment.potencyNumber}${newTreatment.potencyType}`,
          dosage_type: newTreatment.dosageType,
          quantity: parseInt(newTreatment.dosage),
          times: newTreatment.times,
          frequency: frequencyToSave,
          start_date: startDateToSave,
          end_date: formatDateBRT(endDate),
          usage_days: usageDaysToSave,
          pause_days: pauseDaysToSave,
          total_period_months: parseInt(newTreatment.totalPeriod),
          notes: newTreatment.notes || null,
          is_active: true
        };

        if (isEditing) {
          // Atualizar medicação existente
          await familyService.updateMedication(editingMedication.id, medicationData);
        } else {
          // Criar nova medicação
          await familyService.addMedication(member.id, medicationData);

          // Criar evento de saúde positivo no banco (apenas para nova medicação)
          await familyService.addHealthEvent(member.id, {
            event_type: 'positive',
            category: 'treatment',
            description: `Início do tratamento: ${newTreatment.medicationName} ${newTreatment.potencyNumber}${newTreatment.potencyType}`,
            severity: null,
            event_date: newTreatment.startDate
          });

          // Verificar badges após criar primeiro tratamento
          console.log('🏅 [MemberDashboard] Verificando badges após criar tratamento...');
          await checkAndUnlockBadges(member.id, (count) => {
            setBadgesCount(count);
          });

          // Recarregar pontos imediatamente após possíveis badges/nível
          try {
            const previousTotal = memberPoints?.totalPoints ?? 0;
            let refreshed = await pointsService.getMemberPoints(member.id);
            if (!refreshed || refreshed.totalPoints === previousTotal) {
              // Aguarda consistência eventual de triggers
              await new Promise(r => setTimeout(r, 350));
              refreshed = await pointsService.getMemberPoints(member.id);
            }
            if (refreshed) {
              setMemberPoints(refreshed);
              // Atualiza título do nível
              const newTitle = await pointsService.getLevelTitle(refreshed.totalPoints, 'member');
              if (newTitle) {
                setMemberLevelTitle(newTitle);
              } else {
                const fallbackTitle = await pointsService.getLevelTitleByLevel(refreshed.level ?? 1, 'member');
                setMemberLevelTitle(fallbackTitle);
              }
              // Espelha no header simples
              setMember(prev => ({
                ...prev,
                streak: refreshed.currentTreatmentStreak ?? prev.streak,
                points: refreshed.totalPoints ?? prev.points
              }));
              dispatchPointsUpdated();
            }
          } catch (error) {
            console.warn('⚠️ [MemberDashboard] Falha ao recarregar pontos após badge:', error);
          }
        }

        // Recarregar medicações do banco
        const medications = await familyService.getMemberMedications(member.id);
        const formattedMedications = await Promise.all(
          medications.flatMap(async (med) => {
            const { status, progress, daysElapsed, totalDays } = calculateMedicationStatus(med);
            const times = med.times || [];
            
            // Se não tem horários, criar um card padrão
            if (times.length === 0) {
              return [{
                id: med.id,
                name: `${med.remedy_name} ${med.potency || ''}`,
                remedy_name: med.remedy_name,
                potency: med.potency,
                dosage_type: med.dosage_type,
                quantity: med.quantity,
                times: [],
                frequency: med.frequency,
                start_date: med.start_date,
                end_date: med.end_date,
                usage_days: med.usage_days || 30,
                pause_days: med.pause_days || 0,
                total_period_months: med.total_period_months || 3,
                notes: med.notes,
                status,
                progress,
                daysElapsed,
                totalDays,
                taken: false
              }];
            }
            
            // Criar um card para CADA horário
            return Promise.all(times.map(async (timeSlot: string) => {
              const wasTaken = await familyService.checkMedicationTakenToday(med.id, timeSlot);
              
              return {
                id: `${med.id}_${timeSlot}`,
                medicationId: med.id,
                name: `${med.remedy_name} ${med.potency || ''}`,
                remedy_name: med.remedy_name,
                potency: med.potency,
                dosage_type: med.dosage_type,
                quantity: med.quantity,
                times: [timeSlot],
                frequency: med.frequency,
                start_date: med.start_date,
                end_date: med.end_date,
                usage_days: med.usage_days || 30,
                pause_days: med.pause_days || 0,
                total_period_months: med.total_period_months || 3,
                notes: med.notes,
                status,
                progress,
                daysElapsed,
                totalDays,
                taken: wasTaken
              };
            }));
          })
        ).then(results => results.flat());

        // Criar versão agrupada das medicações
        const medicationsGrouped = medications.map(med => {
          const { status, progress, daysElapsed, totalDays } = calculateMedicationStatus(med);
          
          return {
            id: med.id,
            name: `${med.remedy_name} ${med.potency || ''}`,
            remedy_name: med.remedy_name,
            potency: med.potency,
            dosage_type: med.dosage_type,
            quantity: med.quantity,
            times: med.times || [],
            frequency: med.frequency,
            start_date: med.start_date,
            end_date: med.end_date,
            usage_days: med.usage_days || 30,
            pause_days: med.pause_days || 0,
            total_period_months: med.total_period_months || 3,
            notes: med.notes,
            status,
            progress,
            daysElapsed,
            totalDays
          };
        });

        // Recarregar eventos de saúde do banco
        const healthEvents = await familyService.getMemberHealthEvents(member.id);
        const formattedHealthEvents = healthEvents.map((event: any) => ({
          id: event.id,
          type: event.event_type as 'positive' | 'negative',
          description: event.description,
          date: new Date(event.created_at).toLocaleDateString('pt-BR'),
          category: event.category || 'general',
          icon: event.event_type === 'positive' ? 'ri-heart-fill' : 'ri-alert-line'
        }));

        setMember((prev) => ({
          ...prev,
          medications: formattedMedications,
          medicationsGrouped: medicationsGrouped,
          todayDoses: {
            taken: 0,
            total: formattedMedications.filter(m => m.status !== 'completed' && !m.isPaused).length
          },
          healthEvents: formattedHealthEvents
        }));

        // Resetar formulário
        setNewTreatment({
          medicationName: '',
          potencyNumber: '6',
          potencyType: 'CH',
          dosageType: 'globules',
          dosage: '5',
          startDate: getTodayBRT(),
          times: ['08:00'],
          frequency: 'daily',
          customDays: [],
          weeklyDayIndex: null,
          usageDays: '30',
          pauseDays: '7',
          totalPeriod: '3',
          notes: '',
        });

        setShowTreatmentModal(false);
        setEditingMedication(null);
        toast.success(isEditing ? 'Medicação atualizada com sucesso!' : 'Medicação adicionada com sucesso!');
      } catch (error) {
        console.error('❌ Erro ao salvar medicação:', error);
        toast.error('Erro ao salvar medicação. Tente novamente.');
      } finally {
        setSavingTreatment(false);
      }
    }
  };

  const scrollToTreatmentHistory = () => {
    setShowOptionsMenu(false);
    // Aguardar um momento para o menu fechar e então fazer o scroll
    setTimeout(() => {
      const treatmentSection = document.getElementById('treatment-history');
      if (treatmentSection) {
        treatmentSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  const incrementPotencyNumber = () => {
    const currentValue = parseInt(newTreatment.potencyNumber);
    if (currentValue < 1000) {
      setNewTreatment(prev => ({ ...prev, potencyNumber: (currentValue + 1).toString() }));
    }
  };

  const decrementPotencyNumber = () => {
    const currentValue = parseInt(newTreatment.potencyNumber);
    if (currentValue > 1) {
      setNewTreatment(prev => ({ ...prev, potencyNumber: (currentValue - 1).toString() }));
    }
  };

  const incrementDosage = () => {
    const currentValue = parseInt(newTreatment.dosage);
    if (currentValue < 20) {
      setNewTreatment(prev => ({ ...prev, dosage: (currentValue + 1).toString() }));
    }
  };

  const decrementDosage = () => {
    const currentValue = parseInt(newTreatment.dosage);
    if (currentValue > 1) {
      setNewTreatment(prev => ({ ...prev, dosage: (currentValue - 1).toString() }));
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#74935C] flex items-center justify-center font-sans">
        <div className="text-center font-sans">
          <picture>
            <source srcSet="/Imagens/logo-loading.apng" type="image/apng" />
            <img src="/Imagens/logo-loading.gif" alt="Carregando" className="w-16 h-16 mx-auto mb-4 object-contain" />
          </picture>
          <p className="text-white font-medium">Carregando dados do membro...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 pb-20 font-sans">
      {/* Header Personalizado */}
      <div 
        className="relative overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: `url(${memberHeaderBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Overlay para melhorar legibilidade do texto */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        
        <div className="relative z-10 pt-4 px-6">
          <div className="flex items-center justify-between mb-6 mt-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-11 h-11 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <i className="ri-arrow-left-line text-white text-xl"></i>
            </button>
            <h1 className="text-xl font-bold text-white flex-1 text-center mx-4" style={{
              textShadow: '2px 4px 8px rgba(0, 0, 0, 0.6), 0px 2px 4px rgba(0, 0, 0, 0.3)'
            }}>Dashboard Individual</h1>
            <button
              onClick={() => setShowOptionsMenu(!showOptionsMenu)}
              className="w-11 h-11 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center hover:bg-white/30 transition-colors"
              data-tutorial-anchor="member-menu-ellipsis"
            >
              <i className="ri-more-line text-white text-xl"></i>
            </button>
          </div>

          {/* Menu de Opções com Overlay */}
          {showOptionsMenu && (
            <>
              {/* Overlay para fechar menu */}
              <div className="fixed inset-0 z-40" onClick={() => setShowOptionsMenu(false)}></div>
              
              {/* Menu de Opções */}
              <div className="absolute top-20 right-6 bg-white rounded-2xl shadow-xl p-2 z-50 min-w-48">
                <button
                  onClick={scrollToTreatmentHistory}
                  className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                >
                  <MdOutlineHealthAndSafety className="text-green-500 mr-3 text-xl" />
                  <span className="text-sm font-medium drop-shadow-sm">Tratamento</span>
                </button>

                <button
                  onClick={() => {
                    setShowTreatmentModal(true);
                    setShowOptionsMenu(false);
                  }}
                  className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                  data-tutorial-anchor="menu-new-medication"
                >
                  <i className="ri-medicine-bottle-line text-green-500 mr-3 text-lg"></i>
                  <span className="text-sm font-medium drop-shadow-sm">Nova Medicação</span>
                </button>

                <button
                  onClick={() => {
                    setShowAddEventModal(true);
                    setShowOptionsMenu(false);
                  }}
                  className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                >
                  <i className="ri-heart-pulse-line text-emerald-500 mr-3 text-lg"></i>
                  <span className="text-sm font-medium drop-shadow-sm">Como me sinto hoje</span>
                </button>

                <button
                  onClick={() => {
                    setShowAddMedicalModal(true);
                    setShowOptionsMenu(false);
                  }}
                  className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                >
                  <i className="ri-health-book-line text-red-500 mr-3 text-lg"></i>
                  <span className="text-sm font-medium drop-shadow-sm">Registro Médico</span>
                </button>

                <button
                  onClick={() => {
                    setShowAddReminderModal(true);
                    setShowOptionsMenu(false);
                  }}
                  className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                >
                  <i className="ri-notification-3-line text-blue-500 mr-3 text-lg"></i>
                  <span className="text-sm font-medium drop-shadow-sm">Adicionar Lembrete</span>
                </button>



                <button
                  onClick={() => {
                    navigate('/dashboard');
                    setShowOptionsMenu(false);
                  }}
                  className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                >
                  <i className="ri-home-4-line text-gray-500 mr-3 text-lg"></i>
                  <span className="text-sm font-medium drop-shadow-sm">Voltar ao Dashboard</span>
                </button>
              </div>
            </>
          )}

          {/* Perfil do Membro */}
          <div className="text-center mb-6">
            {/* Container para Avatar e Quiz Badge */}
            <div className="relative flex items-center justify-center mb-4">
              {/* Avatar do Membro */}
              <div 
                className="relative w-24 h-24 bg-white/20 rounded-3xl flex items-center justify-center shadow-lg overflow-hidden cursor-pointer hover:scale-105 transition-transform"
                data-quiz-trigger
                onClick={handleQuizClick}
              >
                {!avatarBroken && isImageAvatar(member.avatar) ? (
                  <img 
                    src={getAvatarSrc(member.avatar)} 
                    alt={`Avatar de ${member.name}`} 
                    className="w-full h-full object-cover"
                    onError={() => setAvatarBroken(true)}
                  />
                ) : (
                  <i className={`${member.avatar && member.avatar.startsWith('ri-') ? member.avatar : 'ri-user-line'} text-4xl text-white`}></i>
                )}
              </div>
              
              {/* Quiz Badge posicionado ao lado */}
              <QuizBadgeWrapper 
                memberId={member.id}
                onQuizClick={handleQuizClick}
                className="relative"
              >
                <div></div>
              </QuizBadgeWrapper>
            </div>
            
            <h2 className="text-3xl font-semibold text-white mb-4">{member.name}</h2>

            {/* Card de Pontos do Membro - Simplificado */}
            {memberPoints && (
              <div className="bg-white/20 backdrop-blur-sm rounded-3xl p-5">
                <div className="flex items-center justify-between mb-3">
                  {/* Pontos e Nível */}
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-yellow-400/30 rounded-2xl flex items-center justify-center">
                      <i className="ri-trophy-fill text-yellow-300 text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-white text-2xl font-bold">{memberPoints.totalPoints}</p>
                      <p className="text-white/80 text-xs">Nível {memberPoints.level}</p>
                      {memberLevelTitle && (
                        <p className="text-white/90 text-[11px] font-medium mt-0.5">{memberLevelTitle.title}</p>
                      )}
                    </div>
                  </div>

                  {/* Streaks Importantes */}
                  <div className="flex items-center space-x-4">
                    {/* Dias sem Alopáticos */}
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1 mb-1">
                        <i className="ri-capsule-line text-blue-300 text-lg"></i>
                        <span className="text-white text-xl font-bold">{memberPoints.currentAllopathicStreak}</span>
                      </div>
                      <p className="text-white/70 text-[10px] leading-tight">dias sem<br/>alopáticos</p>
                    </div>

                    {/* Dias sem Doenças */}
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1 mb-1">
                        <i className="ri-heart-pulse-line text-emerald-300 text-lg"></i>
                        <span className="text-white text-xl font-bold">{memberPoints.currentIllnessStreak}</span>
                      </div>
                      <p className="text-white/70 text-[10px] leading-tight">dias sem<br/>doenças</p>
                    </div>
                  </div>
                </div>

                {/* Botões de Ação */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setShowPointsHistoryModal(true)}
                    className="py-2 bg-white/10 hover:bg-white/20 rounded-xl text-white text-xs font-medium transition-colors flex items-center justify-center space-x-1"
                  >
                    <i className="ri-history-line text-sm"></i>
                    <span>Histórico</span>
                  </button>
                  <button
                    onClick={() => setShowBadgeGallery(true)}
                    className="py-2 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 hover:from-yellow-400/30 hover:to-orange-400/30 rounded-xl text-white text-xs font-medium transition-colors flex items-center justify-center space-x-1"
                  >
                    <i className="ri-medal-line text-sm"></i>
                    <span>Meus Badges</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Homeopatias de Hoje */}
      <div className="px-6 py-6">
        <div className="bg-white rounded-3xl p-6 shadow-xl mb-6 relative">
          {hasUrgentDoses > 0 && (
            <div className="absolute -top-3 -right-3 z-10">
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-[0_6px_16px_rgba(239,68,68,0.35)] border border-red-100">
                <MdOutlineNotificationsActive className="h-4 w-4 text-red-500 animate-wiggle" />
      <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs font-bold flex items-center justify-center">
        {hasUrgentDoses > 9 ? '9+' : hasUrgentDoses}
      </div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Homeopatias de Hoje</h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {member.todayDoses.taken}/{member.todayDoses.total}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            {member.medications.length > 0 ? (
              member.medications.map((medication) => {
                const inPause = medication.isPaused ?? false;
                const resumeDate = medication.resumeDate ? new Date(medication.resumeDate) : null;

                const containerClass = `p-4 rounded-2xl border ${inPause ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-transparent'} relative`;
                const iconWrapperClass = `w-10 h-10 rounded-2xl flex items-center justify-center mr-4 ${
                  medication.taken ? 'bg-emerald-100' : inPause ? 'bg-amber-100' : 'bg-gray-200'
                }`;
                const iconClass = medication.taken
                  ? 'ri-check-line text-emerald-600'
                  : inPause
                  ? 'ri-pause-circle-line text-amber-500'
                  : 'ri-medicine-bottle-line text-gray-500';

                return (
                  <div key={medication.id} className={containerClass}>
                    {shouldShowUrgentBellForMedication(medication) && (
                      <div className="absolute -top-2 -right-2">
                        <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-[0_6px_16px_rgba(239,68,68,0.35)] border border-red-100">
                          <MdOutlineNotificationsActive className="h-4 w-4 text-red-500 animate-wiggle" />
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center">
                        <div className={iconWrapperClass}>
                          <i className={`${iconClass} text-lg`}></i>
                        </div>
                        <div>
                          <p className="font-semibold text-base text-gray-800">{medication.name}</p>
                          <p className="text-sm font-normal text-gray-500">
                            {medication.times.length > 0 ? medication.times[0] : 'Sem horário'}
                          </p>
                        </div>
                      </div>

                      {!medication.taken ? (
                        <div className="flex flex-col items-end justify-center gap-1">
                          <div className="flex items-center gap-2">
                            <div data-tutorial-anchor="btn-mark-dose">
                              <Button
                                size="sm"
                                onClick={() => markDoseAsTaken(medication.id)}
                                className="text-xs"
                                disabled={inPause || medication.start_date > getTodayBRT() || medication.status === 'completed'}
                              >{medication.start_date > getTodayBRT() ? 'Preparar' : 'Marcar'}</Button>
                            </div>
                            {inPause && (
                              <span className="text-xs text-amber-600 font-medium">
                                Em pausa
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                          <i className="ri-check-line text-white text-sm"></i>
                        </div>
                      )}
                    </div>
                    {inPause && (
                      <div className="mt-3 text-xs text-amber-600">
                        <p>Este tratamento está em período de pausa.</p>
                        {resumeDate && (
                          <p>Retoma em: {resumeDate.toLocaleDateString('pt-BR')}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-medicine-bottle-line text-gray-400 text-xl"></i>
                </div>
                <p className="text-gray-500 text-base font-normal">Nenhuma medicação cadastrada</p>
                <p className="text-gray-400 text-sm mt-1">Adicione uma nova medicação no menu acima</p>
              </div>
            )}
          </div>
        </div>

        {/* Diário de Saúde */}
        <div className="bg-white rounded-3xl p-6 shadow-xl mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Diário de Saúde</h3>
          </div>
          <p className="text-base font-bold text-gray-600 mb-4">Últimos eventos registrados</p>

          <div className="space-y-3">
            {member.healthEvents.slice(0, 3).map((event) => (
              <div key={event.id} className="flex items-start">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${getCategoryIconColor(event.category, event.type, event.description)}`}>
                  <i className={`${event.icon || getCategoryIcon(event.category, event.type)} text-sm`}></i>
                </div>
                <div className="flex-1">
                  <p className="text-base font-normal text-gray-800">{event.description}</p>
                  <p className="text-base font-normal text-gray-500 mt-1">{event.date}</p>
                </div>
              </div>
            ))}

            {member.healthEvents.length === 0 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-heart-pulse-line text-gray-400 text-xl"></i>
                </div>
                <p className="text-gray-500 text-base font-normal">Nenhum evento registrado ainda</p>
                <p className="text-gray-400 text-base font-normal mt-1">Use "Como me sinto hoje" para adicionar eventos</p>
              </div>
            )}
          </div>

          <button 
            onClick={() => setShowHealthHistoryModal(true)}
            className="w-full mt-4 py-3 text-emerald-600 text-sm font-medium border border-emerald-200 rounded-1xl hover:bg-emerald-50 transition-colors"
          >
            Ver Histórico Completo
          </button>
        </div>

        {/* Registro Médico */}
        <div className="bg-white rounded-3xl p-6 shadow-xl mb-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-3xl flex items-center justify-center mb-4 mx-auto">
              <i className="ri-health-book-line text-red-500 text-2xl"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Registro Médico</h3>
            <p className="text-gray-500 text-base font-normal mb-4">Registre doenças ou medicamentos alopáticos</p>
            <button
              onClick={() => setShowAddMedicalModal(true)}
              className="w-full py-3 bg-red-500 text-white rounded-2xl font-medium hover:bg-red-600 transition-colors"
            >
              <i className="ri-add-line mr-2"></i>
              Fazer Registro Médico
            </button>
          </div>
        </div>

        {/* Log de Lembretes */}
        <div className="bg-white rounded-3xl p-6 shadow-xl mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Log de Lembretes</h3>
            <button
              onClick={() => {
                // Resetar estado para criação
                setEditingReminder(null);
                setNewReminder({
                  title: '',
                  description: '',
                  type: 'once',
                  reminder_date: new Date().toISOString().split('T')[0],
                  reminder_time: '09:00',
                  days_of_week: []
                });
                setShowAddReminderModal(true);
              }}
              className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center"
            >
              <i className="ri-add-line text-blue-600 text-lg"></i>
            </button>
          </div>

          <div className="space-y-3">
            {member.reminders.length > 0 ? (
              member.reminders.slice(0, 4).map((reminder) => {
                const getDaysText = (days: number[]) => {
                  if (!days || days.length === 0) return '';
                  if (days.length === 7) return 'Todos os dias';
                  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
                  return days.map(d => dayNames[d]).join(', ');
                };

                return (
                  <div key={reminder.id} className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 text-sm mb-1">
                          {reminder.title || 'Lembrete'}
                        </h4>
                        {reminder.description && (
                          <p className="text-xs text-gray-600 mb-2">
                            {reminder.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          {reminder.reminder_date ? (
                            // Lembrete único
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-lg flex items-center gap-1">
                              <i className="ri-calendar-check-line"></i>
                              {formatDateBR(reminder.reminder_date)} às {reminder.reminder_time?.substring(0, 5)}
                            </span>
                          ) : (
                            // Lembrete recorrente
                            <>
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-lg flex items-center gap-1">
                                <i className="ri-repeat-line"></i>
                                {getDaysText(reminder.days_of_week || [])}
                              </span>
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-lg flex items-center gap-1">
                                <i className="ri-time-line"></i>
                                {reminder.reminder_time?.substring(0, 5)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            // Abrir modal em modo edição
                            setEditingReminder(reminder);
                            setNewReminder({
                              title: reminder.title || '',
                              description: reminder.description || '',
                              type: reminder.reminder_date ? 'once' : 'recurring',
                              reminder_date: reminder.reminder_date || new Date().toISOString().split('T')[0],
                              reminder_time: (reminder.reminder_time || '09:00:00').substring(0,5),
                              days_of_week: reminder.days_of_week || []
                            });
                            setShowAddReminderModal(true);
                          }}
                          className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                          title="Editar lembrete"
                        >
                          <i className="ri-pencil-line text-sm"></i>
                        </button>
                        <button
                        onClick={async () => {
                          if (confirm('Deseja realmente excluir este lembrete?')) {
                            try {
                              console.log('🗑️ Deletando lembrete:', reminder.id);
                              await familyService.deleteReminder(reminder.id);
                              // Limpar filas deste lembrete (não bloqueante)
                              try {
                                if (user?.id) {
                                  await supabase
                                    .from('notification_queue' as any)
                                    .delete()
                                    .eq('user_id', user.id)
                                    .filter('data->>reminder_id', 'eq', reminder.id);
                                }
                              } catch (error) {
                                console.warn('⚠️ Falha ao limpar filas do lembrete deletado (não bloqueante):', error);
                              }
                              
                              // Recarregar lembretes
                              const reminders = await familyService.getMemberReminders(member.id);
                              const formattedReminders = reminders.map(r => ({
                                id: r.id,
                                title: r.title,
                                description: r.description,
                                reminder_date: r.reminder_date,
                                reminder_time: r.reminder_time,
                                days_of_week: r.days_of_week,
                                created_at: r.created_at
                              }));
                              
                              setMember(prev => ({
                                ...prev,
                                reminders: formattedReminders
                              }));
                              
                              console.log('✅ Lembrete deletado com sucesso!');
                            } catch (error) {
                              console.error('❌ Erro ao deletar lembrete:', error);
                              toast.error('Erro ao deletar lembrete. Tente novamente.');
                            }
                          }
                        }}
                        className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-notification-3-line text-gray-400 text-xl"></i>
                </div>
                <p className="text-gray-500 text-base font-normal">Nenhum lembrete cadastrado</p>
                <p className="text-gray-400 text-sm mt-1">Clique no + acima para adicionar</p>
              </div>
            )}
          </div>

          {member.reminders.length > 0 && (
            <button 
              onClick={() => setShowAllRemindersModal(true)}
              className="w-full mt-4 py-3 text-blue-600 text-sm font-medium border border-blue-200 rounded-2xl hover:bg-blue-50 transition-colors"
            >
              Ver Todos os Lembretes ({member.reminders.length})
            </button>
          )}
        </div>

        {/* Estatísticas Rápidas */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-lg text-center">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 mx-auto ${
              memberAdherence && memberAdherence.adherence_percentage >= 75 
                ? 'bg-emerald-100' 
                : memberAdherence && memberAdherence.adherence_percentage >= 50
                ? 'bg-yellow-100'
                : 'bg-red-100'
            }`}>
              <i className={`ri-calendar-check-line text-lg ${
                memberAdherence && memberAdherence.adherence_percentage >= 75 
                  ? 'text-emerald-600' 
                  : memberAdherence && memberAdherence.adherence_percentage >= 50
                  ? 'text-yellow-600'
                  : 'text-red-600'
              }`}></i>
            </div>
            <p className={`text-base font-bold ${
              memberAdherence && memberAdherence.adherence_percentage >= 75 
                ? 'text-emerald-600' 
                : memberAdherence && memberAdherence.adherence_percentage >= 50
                ? 'text-yellow-600'
                : 'text-red-600'
            }`}>
              {memberAdherence ? `${Math.round(memberAdherence.adherence_percentage)}%` : '0%'}
            </p>
            <p className="text-base font-normal text-gray-600">Adesão</p>
          </div>

          <div className="bg-white rounded-2xl p-4 shadow-lg text-center">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center mb-2 mx-auto">
              <i className="ri-fire-fill text-orange-500 text-lg"></i>
            </div>
            <p className="text-base font-bold text-gray-800">{memberPoints?.currentTreatmentStreak ?? 0}</p>
            <p className="text-base font-normal text-gray-600">Dias sem interrupção</p>
          </div>

          <div 
            className="bg-white rounded-2xl p-4 shadow-lg text-center cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => setShowBadgeGallery(true)}
          >
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mb-2 mx-auto">
              <i className="ri-trophy-line text-amber-600 text-lg"></i>
            </div>
            <p className="text-base font-bold text-gray-800">{badgesCount}</p>
            <p className="text-base font-normal text-gray-600">Conquistas</p>
          </div>
        </div>

        {/* Log de Tratamentos */}
        <div id="treatment-history" className="bg-white rounded-3xl p-6 shadow-xl mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Histórico de Tratamentos</h3>
            <button
              onClick={() => setShowTreatmentModal(true)}
              className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center"
            >
              <i className="ri-add-line text-green-600 text-lg"></i>
            </button>
          </div>

          <div className="space-y-4">
            {member.medicationsGrouped.length > 0 ? (
              [...member.medicationsGrouped]
                .sort((a, b) => (a.status === 'completed' ? 1 : 0) - (b.status === 'completed' ? 1 : 0))
                .map((medication) => {
                // Render compacto para concluídos
                if (medication.status === 'completed') {
                  return (
                    <div key={medication.id} className="border-l-4 border-gray-300 pl-4 py-3 relative">
                      <div className="flex items-center justify-between">
                        <button
                          className="font-semibold text-gray-800 text-base text-left hover:underline"
                          onClick={() => { setSummaryMedicationId(medication.id); setShowTreatmentSummaryModal(true); }}
                        >
                          {medication.name}
                        </button>
                        <span className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full font-medium">Concluído</span>
                      </div>
                    </div>
                  );
                }

                // Definir cores baseadas no status
                const statusConfig = {
                  active: {
                    borderColor: 'border-green-500',
                    bgColor: 'bg-green-100',
                    textColor: 'text-green-600',
                    barColor: 'bg-green-500',
                    label: 'Ativo'
                  },
                  paused: {
                    borderColor: 'border-amber-400',
                    bgColor: 'bg-amber-100',
                    textColor: 'text-amber-600',
                    barColor: 'bg-amber-400',
                    label: 'Pausa'
                  },
                  completed: {
                    borderColor: 'border-gray-300',
                    bgColor: 'bg-gray-100',
                    textColor: 'text-gray-600',
                    barColor: 'bg-gray-400',
                    label: 'Concluído'
                  }
                ,
                  upcoming: {
                    borderColor: 'border-blue-500',
                    bgColor: 'bg-blue-100',
                    textColor: 'text-blue-600',
                    barColor: 'bg-blue-500',
                    label: 'Preparar'
                  }};

                const config = statusConfig[medication.status] || {
  borderColor: 'border-blue-500',
  bgColor: 'bg-blue-100',
  textColor: 'text-blue-600',
  barColor: 'bg-blue-500',
  label: 'Preparar'
};
                const dosageText = medication.dosage_type === 'globules' 
                  ? `${medication.quantity} glóbulos` 
                  : `${medication.quantity} gotas`;
                
                const timesText = medication.times.length > 0 
                  ? medication.times.join(', ') 
                  : 'Não definido';

                const frequencyText = (() => {
                  const f = (medication.frequency || '').toString();
                  
                  // Parse weekly;N → "Semanal (Ter)"
                  if (f.startsWith('weekly;')) {
                    const parts = f.split(';');
                    const idx = Number(parts[1]);
                    const day = (!Number.isNaN(idx) && weekDays[idx]) ? weekDays[idx] : (parts[1] || '');
                    return day ? 'Semanal (' + day + ')' : 'Semanal';
                  }
                  
                  // Parse custom;1,3,4 → "Personalizado (Seg, Qua, Qui)"
                  if (f.startsWith('custom;')) {
                    const parts = f.split(';');
                    if (parts[1]) {
                      const dayIndices = parts[1].split(',').map((idx: string) => parseInt(idx));
                      const dayNames = dayIndices
                        .map((idx: number) => weekDays[idx])
                        .filter(Boolean)
                        .join(', ');
                      return dayNames ? 'Personalizado (' + dayNames + ')' : 'Personalizado';
                    }
                    return 'Personalizado';
                  }
                  
                  return ({
                    'daily': 'Diário',
                    'alternate': 'Dias alternados',
                    'weekly': 'Semanal',
                    'custom': 'Personalizado'
                  } as Record<string, string>)[f] || f;
                })();

                return (
                  <div key={medication.id} className={`border-l-4 ${config.borderColor} pl-4 py-3 relative`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-800 text-base flex-1">{medication.name}</h4>
                      <div className="flex items-center gap-2">
                        <span className={`${config.bgColor} ${config.textColor} text-xs px-3 py-1 rounded-full font-medium`}>
                          {medication.start_date > getTodayBRT() ? 'Preparar' : config.label}
                        </span>
                        <button
                          onClick={() => handleEditMedication(medication)}
                          className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center hover:bg-blue-200 transition-colors"
                          title="Editar medicação"
                        >
                          <i className="ri-pencil-line text-blue-600 text-sm"></i>
                        </button>
                        <button
                          onClick={async () => {
                            if (confirm(`Deseja realmente excluir ${medication.name}?`)) {
                              try {
                                await familyService.deleteMedication(medication.id);
                                
                                // Recarregar medicações
                                const medications = await familyService.getMemberMedications(member.id);
                                
                                // Criar versão expandida (um card por horário)
                                const formattedMedications = await Promise.all(
                                  medications.flatMap(async (med) => {
                                    const { status, progress, daysElapsed, totalDays } = calculateMedicationStatus(med);
                                    const times = med.times || [];
                                    
                                    if (times.length === 0) {
                                      return [{
                                        id: med.id,
                                        name: `${med.remedy_name} ${med.potency || ''}`,
                                        remedy_name: med.remedy_name,
                                        potency: med.potency,
                                        dosage_type: med.dosage_type,
                                        quantity: med.quantity,
                                        times: [],
                                        frequency: med.frequency,
                                        start_date: med.start_date,
                                        end_date: med.end_date,
                                        usage_days: med.usage_days || 30,
                                        pause_days: med.pause_days || 0,
                                        total_period_months: med.total_period_months || 3,
                                        notes: med.notes,
                                        status,
                                        progress,
                                        daysElapsed,
                                        totalDays,
                                        taken: false
                                      }];
                                    }
                                    
                                    return Promise.all(times.map(async (timeSlot: string) => {
                                      const wasTaken = await familyService.checkMedicationTakenToday(med.id, timeSlot);
                                      
                                      return {
                                        id: `${med.id}_${timeSlot}`,
                                        medicationId: med.id,
                                        name: `${med.remedy_name} ${med.potency || ''}`,
                                        remedy_name: med.remedy_name,
                                        potency: med.potency,
                                        dosage_type: med.dosage_type,
                                        quantity: med.quantity,
                                        times: [timeSlot],
                                        frequency: med.frequency,
                                        start_date: med.start_date,
                                        end_date: med.end_date,
                                        usage_days: med.usage_days || 30,
                                        pause_days: med.pause_days || 0,
                                        total_period_months: med.total_period_months || 3,
                                        notes: med.notes,
                                        status,
                                        progress,
                                        daysElapsed,
                                        totalDays,
                                        taken: wasTaken
                                      };
                                    }));
                                  })
                                ).then(results => results.flat());
                                
                                // Criar versão agrupada (um card por medicação)
                                const medicationsGrouped = medications.map(med => {
                                  const { status, progress, daysElapsed, totalDays } = calculateMedicationStatus(med);
                                  
                                  return {
                                    id: med.id,
                                    name: `${med.remedy_name} ${med.potency || ''}`,
                                    remedy_name: med.remedy_name,
                                    potency: med.potency,
                                    dosage_type: med.dosage_type,
                                    quantity: med.quantity,
                                    times: med.times || [],
                                    frequency: med.frequency,
                                    start_date: med.start_date,
                                    end_date: med.end_date,
                                    usage_days: med.usage_days || 30,
                                    pause_days: med.pause_days || 0,
                                    total_period_months: med.total_period_months || 3,
                                    notes: med.notes,
                                    status,
                                    progress,
                                    daysElapsed,
                                    totalDays
                                  };
                                });

                                const nonCompletedAfterDelete = formattedMedications.filter(m => m.status !== 'completed');
                                setMember((prev) => ({
                                  ...prev,
                                  medications: nonCompletedAfterDelete,
                                  medicationsGrouped: medicationsGrouped,
                                  todayDoses: {
                                    taken: 0,
                                    total: nonCompletedAfterDelete.filter(m => !m.isPaused).length
                                  }
                                }));

                                toast.info('Medicação excluída com sucesso!');
                              } catch (error) {
                                console.error('❌ Erro ao excluir medicação:', error);
                                toast.error('Erro ao excluir medicação. Tente novamente.');
                              }
                            }
                          }}
                          className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center hover:bg-red-200 transition-colors"
                          title="Excluir medicação"
                        >
                          <i className="ri-delete-bin-line text-red-600 text-sm"></i>
                        </button>
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-600 space-y-1">
                      <p>• {dosageText}, {timesText} {medication.times.length > 1 ? `(${medication.times.length} vezes ao dia)` : ''}</p>
                      <p>• Frequência: {frequencyText}</p>
                      <p>• Duração: {medication.usage_days} dias de uso{medication.pause_days > 0 ? `, ${medication.pause_days} dias de pausa` : ''}</p>
                      <p>• Período total: {medication.total_period_months} {medication.total_period_months === 1 ? 'mês' : 'meses'}</p>
                      <p>• {medication.start_date > getTodayBRT() ? 'Iniciar em' : 'Iniciado em'}: {formatDateDisplay(medication.start_date)}</p>
                      {medication.notes && <p>• Observações: {medication.notes}</p>}
                    </div>

                    {/* Barra de Progresso */}
                    <div className="mt-4">
                      <div className="w-full bg-slate-200 rounded-full h-3.5 overflow-hidden">
                        <div 
                          className={`${config.barColor} h-full rounded-full transition-all duration-300`}
                          style={{ width: `${Math.max(medication.progress, 1)}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-slate-500 mt-2 font-medium">
                        {medication.daysElapsed}/{medication.totalDays} dias completos
                      </p>
                      {adherenceMap && adherenceMap[medication.id] !== undefined && (
                        <p className={`text-sm mt-1 font-medium ${adherenceMap[medication.id] >= 75 ? 'text-emerald-600' : (adherenceMap[medication.id] >= 50 ? 'text-yellow-600' : 'text-red-600')}`}>
                          Aderência ao tratamento: {Math.round(adherenceMap[medication.id])}%
                        </p>
                      )}
                    </div>
                  </div>
                );
              
              })
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-medicine-bottle-line text-gray-400 text-xl"></i>
                </div>
                <p className="text-gray-500 text-base font-normal">Nenhum tratamento cadastrado</p>
                <p className="text-gray-400 text-sm mt-1">Adicione uma nova medicação abaixo</p>
              </div>
            )}
          </div>

          <button 
            onClick={() => setShowTreatmentModal(true)}
            className="w-full mt-4 py-3 text-green-600 text-sm font-medium border border-green-200 rounded-2xl hover:bg-green-50 transition-colors"
          >
            <i className="ri-add-line mr-2"></i>
            Adicionar Nova Medicação
          </button>
        </div>
      </div>

      <BottomNavigation />

      {/* Modal Adicionar Registro Médico */}
      {showAddMedicalModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800">Registro Médico</h3>
              <button
                onClick={() => setShowAddMedicalModal(false)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
              >
                <i className="ri-close-line text-gray-600"></i>
              </button>
            </div>

            {/* Seletor de Tipo */}
            <div className="mb-6">
              <p className="text-base font-normal text-gray-700 mb-3 drop-shadow-sm">Tipo de Registro</p>
              <div className="flex gap-3">
                <button
                  onClick={() =>
                    setNewMedicalRecord((prev) => ({ ...prev, type: 'illness', medicationType: undefined }))
                  }
                  className={`flex-1 py-3 px-4 rounded-2xl flex items-center justify-center transition-colors ${
                    newMedicalRecord.type === 'illness'
                      ? 'bg-red-100 border-2 border-red-300'
                      : 'bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <i className="ri-virus-line text-red-500 mr-2"></i>
                  <span className="text-sm font-medium text-gray-800 drop-shadow-sm">Doença</span>
                </button>
                <button
                  onClick={() =>
                    setNewMedicalRecord((prev) => ({
                      ...prev,
                      type: 'allopathic_medication',
                      severity: undefined,
                    }))
                  }
                  className={`flex-1 py-3 px-4 rounded-2xl flex items-center justify-center transition-colors ${
                    newMedicalRecord.type === 'allopathic_medication'
                      ? 'bg-orange-100 border-2 border-orange-300'
                      : 'bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <i className="ri-medicine-bottle-line text-orange-500 mr-1"></i>
                  <span className="text-sm font-medium text-gray-800 drop-shadow-sm">Medicamento</span>
                </button>
              </div>
            </div>

            {/* Seletor de Gravidade para Doenças */}
            {newMedicalRecord.type === 'illness' && (
              <div className="mb-6">
                <p className="text-base font-normal text-gray-700 mb-3 drop-shadow-sm">Gravidade</p>
                <div className="flex gap-2">
                  {(['leve', 'moderado', 'alto'] as const).map((severity) => (
                    <button
                      key={severity}
                      onClick={() => setNewMedicalRecord((prev) => ({ ...prev, severity }))}
                      className={`flex-1 py-2 px-3 rounded-xl text-xs font-medium transition-colors ${
                        newMedicalRecord.severity === severity
                          ? `${getSeverityColor(severity)} border-2 border-current`
                          : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                      }`}
                    >
                      {severity === 'leve' ? 'Leve' : severity === 'moderado' ? 'Moderado' : 'Alto'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Seletor de Tipo de Medicamento */}
            {newMedicalRecord.type === 'allopathic_medication' && (
              <div className="mb-6">
                <p className="text-base font-normal text-gray-700 mb-3 drop-shadow-sm">Tipo de Medicamento</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    'Antibiótico',
                    'Anti-inflamatório',
                    'Analgésico',
                    'Corticoide',
                    'Estatina',
                    'Anti-hipertensivo',
                    'Antidepressivo',
                    'Ansiolítico',
                    'Diurético',
                    'Broncodilatador',
                    'Antiácido',
                    'Outro',
                  ].map((type) => (
                    <button
                      key={type}
                      onClick={() => setNewMedicalRecord((prev) => ({ ...prev, medicationType: type }))}
                      className={`py-2 px-3 rounded-xl text-xs font-medium transition-colors ${
                        newMedicalRecord.medicationType === type
                          ? 'bg-orange-100 text-orange-600 border-2 border-orange-300'
                          : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Campo de Descrição */}
            <div className="mb-6">
              <p className="text-base font-normal text-gray-700 mb-3 drop-shadow-sm">Descrição</p>
              <textarea
                value={newMedicalRecord.description}
                onChange={(e) => setNewMedicalRecord((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={
                  newMedicalRecord.type === 'illness'
                    ? 'Ex: Gripe com febre, dor de garganta...'
                    : 'Ex: Dipirona 500mg, Ibuprofeno 600mg...'
                }
                className="w-full h-24 p-4 border border-gray-200 rounded-2xl resize-none text-sm"
                maxLength={150}
              />
              <p className="text-sm font-medium text-gray-500 mt-2 text-right drop-shadow-sm">
                {newMedicalRecord.description.length}/150
              </p>
            </div>

            {/* Botões */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddMedicalModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-2xl font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddMedicalRecord}
                disabled={
                  !newMedicalRecord.description.trim() ||
                  (newMedicalRecord.type === 'allopathic_medication' && !newMedicalRecord.medicationType)
                }
                className="flex-1 py-3 bg-red-500 text-white rounded-2xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar Lembrete */}
      {showAddReminderModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800">{editingReminder ? 'Editar Lembrete' : 'Novo Lembrete'}</h3>
              <button
                onClick={() => { setShowAddReminderModal(false); setEditingReminder(null); }}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
              >
                <i className="ri-close-line text-gray-600"></i>
              </button>
            </div>

            {/* Campo de Título */}
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Título</label>
              <input
                type="text"
                value={newReminder.title}
                onChange={(e) => setNewReminder(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ex: Consulta médica, Tomar vitamina..."
                className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                maxLength={50}
              />
            </div>

            {/* Campo de Descrição */}
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Descrição (opcional)</label>
              <textarea
                value={newReminder.description}
                onChange={(e) => setNewReminder(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ex: Levar exames anteriores, após o café..."
                className="w-full h-20 p-3 border border-gray-200 rounded-xl resize-none text-sm"
                maxLength={150}
              />
              <p className="text-xs text-gray-500 mt-1 text-right">
                {newReminder.description.length}/150
              </p>
            </div>

            {/* Tipo de Lembrete */}
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Tipo de Lembrete</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setNewReminder(prev => ({ ...prev, type: 'once' }))}
                  className={`flex-1 py-2 px-4 rounded-xl flex items-center justify-center transition-colors ${
                    newReminder.type === 'once' 
                      ? 'bg-blue-100 border-2 border-blue-500' 
                      : 'bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <i className="ri-calendar-check-line text-blue-600 mr-2"></i>
                  <span className="text-sm font-medium text-gray-800">Único</span>
                </button>
                <button
                  onClick={() => setNewReminder(prev => ({ ...prev, type: 'recurring' }))}
                  className={`flex-1 py-2 px-4 rounded-xl flex items-center justify-center transition-colors ${
                    newReminder.type === 'recurring' 
                      ? 'bg-purple-100 border-2 border-purple-500' 
                      : 'bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <i className="ri-repeat-line text-purple-600 mr-2"></i>
                  <span className="text-sm font-medium text-gray-800">Repetir</span>
                </button>
              </div>
            </div>

            {newReminder.type === 'once' ? (
              // Lembrete único - Data e hora
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Data e Hora</label>
                <div className="flex gap-3">
                  <input
                    type="date"
                    value={newReminder.reminder_date}
                    onChange={(e) => setNewReminder(prev => ({ ...prev, reminder_date: e.target.value }))}
                    className="flex-1 p-3 border border-gray-200 rounded-xl text-sm"
                  />
                  <input
                    type="time"
                    value={newReminder.reminder_time}
                    onChange={(e) => setNewReminder(prev => ({ ...prev, reminder_time: e.target.value }))}
                    className="flex-1 p-3 border border-gray-200 rounded-xl text-sm"
                  />
                </div>
              </div>
            ) : (
              // Lembrete recorrente - Dias da semana e hora
              <>
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Repetir em:</label>
                  <div className="flex gap-2 justify-between">
                    {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          const days = newReminder.days_of_week || [];
                          const newDays = days.includes(index)
                            ? days.filter(d => d !== index)
                            : [...days, index].sort();
                          setNewReminder(prev => ({ ...prev, days_of_week: newDays }));
                        }}
                        className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                          (newReminder.days_of_week || []).includes(index)
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Horário</label>
                  <input
                    type="time"
                    value={newReminder.reminder_time}
                    onChange={(e) => setNewReminder(prev => ({ ...prev, reminder_time: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm"
                  />
                </div>
              </>
            )}

            {/* Botões */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddReminderModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-2xl font-medium hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveReminder}
                disabled={!newReminder.title.trim() || (newReminder.type === 'recurring' && newReminder.days_of_week.length === 0)}
                className="flex-1 py-3 bg-blue-500 text-white rounded-2xl font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors"
              >
                {editingReminder ? 'Salvar' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ver Todos os Lembretes */}
      {showAllRemindersModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-800">Todos os Lembretes</h3>
              <button
                onClick={() => setShowAllRemindersModal(false)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
              >
                <i className="ri-close-line text-gray-600"></i>
              </button>
            </div>

            <div className="space-y-3">
              {member.reminders.map((reminder) => {
                const getDaysText = (days: number[]) => {
                  if (!days || days.length === 0) return '';
                  if (days.length === 7) return 'Todos os dias';
                  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
                  return days.map(d => dayNames[d]).join(', ');
                };

                return (
                  <div key={reminder.id} className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 text-base mb-1">
                          {reminder.title || 'Lembrete'}
                        </h4>
                        {reminder.description && (
                          <p className="text-sm text-gray-600 mb-3">
                            {reminder.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          {reminder.reminder_date ? (
                            <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-lg flex items-center gap-1">
                              <i className="ri-calendar-check-line"></i>
                              {formatDateBR(reminder.reminder_date)} às {reminder.reminder_time?.substring(0, 5)}
                            </span>
                          ) : (
                            <>
                              <span className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-lg flex items-center gap-1">
                                <i className="ri-repeat-line"></i>
                                {getDaysText(reminder.days_of_week || [])}
                              </span>
                              <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-lg flex items-center gap-1">
                                <i className="ri-time-line"></i>
                                {reminder.reminder_time?.substring(0, 5)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setEditingReminder(reminder);
                            setNewReminder({
                              title: reminder.title || '',
                              description: reminder.description || '',
                              type: reminder.reminder_date ? 'once' : 'recurring',
                              reminder_date: reminder.reminder_date || new Date().toISOString().split('T')[0],
                              reminder_time: (reminder.reminder_time || '09:00:00').substring(0,5),
                              days_of_week: reminder.days_of_week || []
                            });
                            setShowAddReminderModal(true);
                          }}
                          className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                          title="Editar lembrete"
                        >
                          <i className="ri-pencil-line"></i>
                        </button>
                        <button
                        onClick={async () => {
                          if (confirm('Deseja realmente excluir este lembrete?')) {
                            try {
                              await familyService.deleteReminder(reminder.id);
                              // Limpar filas deste lembrete (não bloqueante)
                              try {
                                if (user?.id) {
                                  await supabase
                                    .from('notification_queue' as any)
                                    .delete()
                                    .eq('user_id', user.id)
                                    .filter('data->>reminder_id', 'eq', reminder.id);
                                }
                              } catch (error) {
                                console.warn('⚠️ Falha ao limpar filas do lembrete deletado (não bloqueante):', error);
                              }
                              const reminders = await familyService.getMemberReminders(member.id);
                              const formattedReminders = reminders.map(r => ({
                                id: r.id,
                                title: r.title,
                                description: r.description,
                                reminder_date: r.reminder_date,
                                reminder_time: r.reminder_time,
                                days_of_week: r.days_of_week,
                                created_at: r.created_at
                              }));
                              setMember(prev => ({ ...prev, reminders: formattedReminders }));
                              if (formattedReminders.length === 0) {
                                setShowAllRemindersModal(false);
                              }
                            } catch (error) {
                              console.error('Erro ao deletar:', error);
                              toast.error('Erro ao deletar lembrete');
                            }
                          }
                        }}
                        className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors ml-2"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar Eventos de Saúde */}
      {showAddEventModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800">Novo Registro</h3>
              <button
                onClick={() => setShowAddEventModal(false)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
              >
                <i className="ri-close-line text-gray-600"></i>
              </button>
            </div>

            {/* Seletor de Tipo */}
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3 drop-shadow-sm">Tipo de Registro</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setNewEvent((prev) => ({ ...prev, type: 'positive' }))}
                  className={`flex-1 py-3 px-4 rounded-2xl flex items-center justify-center transition-colors ${
                    newEvent.type === 'positive'
                      ? 'bg-emerald-100 border-2 border-emerald-300'
                      : 'bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <i className="ri-heart-fill text-emerald-500 mr-2"></i>
                  <span className="text-sm font-medium text-gray-800 drop-shadow-sm">Positivo</span>
                </button>
                <button
                  onClick={() => setNewEvent((prev) => ({ ...prev, type: 'negative' }))}
                  className={`flex-1 py-3 px-4 rounded-2xl flex items-center justify-center transition-colors ${
                    newEvent.type === 'negative'
                      ? 'bg-red-100 border-2 border-red-300'
                      : 'bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <i className="ri-alert-line text-red-500 mr-2"></i>
                  <span className="text-sm font-medium text-gray-800 drop-shadow-sm">Sintoma</span>
                </button>
              </div>
            </div>

            {/* Campo de Descrição */}
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3 drop-shadow-sm">Descrição</p>
              <textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={
                  newEvent.type === 'positive'
                    ? 'Ex: Dormi bem, me senti disposto hoje...'
                    : 'Ex: Dor de cabeça leve, cansaço...'
                }
                className="w-full h-24 p-4 border border-gray-200 rounded-2xl resize-none text-sm"
                maxLength={150}
              />
              <p className="text-xs text-gray-500 mt-2 text-right drop-shadow-sm">
                {newEvent.description.length}/150
              </p>
            </div>

            {/* Botões */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddEventModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-2xl font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddHealthEvent}
                disabled={!newEvent.description.trim()}
                className="flex-1 py-3 bg-emerald-500 text-white rounded-2xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Tratamento */}
      {showTreatmentModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[calc(100vh-120px)] overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-100 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">
                  {editingMedication ? 'Editar medicação' : 'Nova medicação'}
                </h3>
                <button
                  onClick={() => {
                    setShowTreatmentModal(false);
                    setEditingMedication(null);
                  }}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* NOVO: Seção Preenchimento Automático */}
              <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl border-2 border-purple-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <i className="ri-robot-line text-2xl text-purple-600"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 text-base">
                      Preenchimento Automático
                    </h4>
                    <p className="text-xs text-gray-600">
                      Envie uma foto da prescrição médica
                    </p>
                  </div>
                </div>
                
                <input
                  type="file"
                  id="prescription-upload"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handlePrescriptionUpload}
                  className="hidden"
                  disabled={analyzingPrescription}
                />
                
                <div className="flex gap-2">
                  <label
                    htmlFor="prescription-upload"
                    className={`flex-1 py-3 px-4 rounded-xl text-center font-medium transition-all cursor-pointer ${
                      analyzingPrescription
                        ? 'bg-green-100 text-green-700 cursor-not-allowed'
                        : 'bg-purple-500 text-white hover:bg-purple-600 active:scale-95'
                    }`}
                  >
                    {analyzingPrescription ? (
                      <>
                        <i className="ri-loader-2-line animate-spin mr-2"></i>
                        Analisando prescrição...
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-cloud-line mr-2"></i>
                        Carregar Receita
                      </>
                    )}
                  </label>
                  
                  {hasCachedData && prescriptionMedications.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        clearPrescription(member.id);
                        toast.info('Análise limpa. Faça novo upload se necessário.');
                      }}
                      className="px-4 py-3 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                      title="Limpar análise em cache"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  )}
                </div>
                
                {/* Erro de análise */}
                {prescriptionAnalysisError && (
                  <div className="mt-3 p-3 bg-red-50 rounded-xl border border-red-200">
                    <p className="text-sm text-red-600">
                      <i className="ri-error-warning-line mr-1"></i>
                      {prescriptionAnalysisError}
                    </p>
                  </div>
                )}
                
                {/* Seletor de medicamentos (quando houver múltiplos) */}
                {prescriptionMedications.length > 1 && (
                  <div className="mt-3 space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Selecione o medicamento para adicionar:
                    </label>
                    <div className="grid gap-2">
                      {prescriptionMedications.map((med, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => applySelectedMedication(index)}
                          className={`p-3 rounded-xl text-left transition-all ${
                            selectedMedicationIndex === index
                              ? 'bg-purple-100 border-2 border-purple-500'
                              : 'bg-white border-2 border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-gray-800">
                                {med.remedy_name || 'Medicamento sem nome'}
                              </p>
                              <p className="text-sm text-gray-600">
                                {med.potency_number}{med.potency_type} - {med.quantity} {med.dosage_type === 'globules' ? 'glóbulos' : 'gotas'}
                              </p>
                            </div>
                            {selectedMedicationIndex === index && (
                              <i className="ri-check-line text-2xl text-purple-600"></i>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Disclaimer de responsabilidade */}
                {prescriptionMedications.length > 0 && !prescriptionAnalysisError && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                    <p className="text-sm text-blue-800 font-medium mb-2">
                      <i className="ri-information-line mr-1"></i>
                      Atenção: Conferência obrigatória
                    </p>
                    <p className="text-xs text-blue-700 leading-relaxed">
                      O sistema preencheu os campos automaticamente, mas a <strong>conferência 
                      das informações é de sua responsabilidade</strong>. Por favor, 
                      <strong> revise todos os dados</strong> antes de criar o tratamento. 
                      Caso precise, você consegue editar as informações.
                    </p>
                  </div>
                )}
              </div>

              {/* Nome do Medicamento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Nome do Medicamento Homeopático *
                </label>
                <input
                  type="text"
                  value={newTreatment.medicationName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setNewTreatment((prev) => ({ ...prev, medicationName: v }));
                    try { if (v && v.trim().length > 0) { window.dispatchEvent(new CustomEvent('tutorial:med:name-filled')); } } catch { /* ignore */ }
                  }}
                  placeholder="Ex: Arnica Montana, Nux Vomica..."
                  className="w-full p-4 border border-gray-200 rounded-2xl text-sm focus:border-green-300 focus:outline-none"
                  data-tutorial-anchor="med-name"
                />
              </div>

              {/* Potência */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Potência *
                </label>
                
                {/* Contador/Stepper para o Número */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Valor da Potência (1 a 1000)
                  </label>
                  <div className="flex items-center justify-center bg-gray-50 rounded-2xl p-2" data-tutorial-anchor="med-potency-number">
                    <button
                      type="button"
                      onClick={() => { decrementPotencyNumber(); try { window.dispatchEvent(new CustomEvent('tutorial:med:potency-number-filled')); } catch { /* ignore */ } }}
                      disabled={parseInt(newTreatment.potencyNumber) <= 1}
                      className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      <i className="ri-subtract-line text-gray-600"></i>
                    </button>
                    
                    <div className="flex-1 text-center mx-4">
                      <input
                        type="number"
                        value={newTreatment.potencyNumber}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          const clampedValue = Math.max(1, Math.min(1000, value));
                          setNewTreatment(prev => ({ ...prev, potencyNumber: clampedValue.toString() }));
                          try { window.dispatchEvent(new CustomEvent('tutorial:med:potency-number-filled')); } catch { /* ignore */ }
                        }}
                        min="1"
                        max="1000"
                        className="w-full text-center text-2xl font-bold text-gray-800 bg-transparent border-none outline-none"
                      />
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => { incrementPotencyNumber(); try { window.dispatchEvent(new CustomEvent('tutorial:med:potency-number-filled')); } catch { /* ignore */ } }}
                      disabled={parseInt(newTreatment.potencyNumber) >= 1000}
                      className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      <i className="ri-add-line text-gray-600"></i>
                    </button>
                  </div>
                  
                  {/* Botões de acesso rápido para valores comuns */}
                  <div className="mt-3 flex flex-wrap gap-2 justify-center">
                    {[3, 6, 12, 30, 200].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => { setNewTreatment(prev => ({ ...prev, potencyNumber: value.toString() })); try { window.dispatchEvent(new CustomEvent('tutorial:med:potency-number-filled')); } catch { /* ignore */ } }}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                          newTreatment.potencyNumber === value.toString()
                            ? 'bg-green-100 text-green-600 border border-green-300'
                            : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Botões de Tipo de Potência */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Tipo de Potência
                  </label>
                  <div className="grid grid-cols-3 gap-3" data-tutorial-anchor="med-potency-type">
                    {(['DH', 'CH', 'M'] as const).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => { setNewTreatment(prev => ({ ...prev, potencyType: type })); try { window.dispatchEvent(new CustomEvent('tutorial:med:potency-type-selected')); } catch { /* ignore */ } }}
                        className={`py-3 px-4 rounded-xl text-sm font-medium transition-colors ${
                          newTreatment.potencyType === type
                            ? 'bg-green-100 text-green-600 border-2 border-green-300'
                            : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                  
                  {/* Visualização da Potência Completa */}
                  <div className="mt-3 text-center">
                    <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-xl">
                      <i className="ri-flask-line text-blue-500 mr-2"></i>
                      <span className="text-blue-700 font-medium drop-shadow-sm">
                        {newTreatment.potencyNumber}{newTreatment.potencyType}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Informações sobre Potências */}
                <div className="mt-4 p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-600 mb-2 drop-shadow-sm">
                    <strong>Tipos de Potência:</strong>
                  </p>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p className="drop-shadow-sm">• <strong>DH:</strong> Decimal (1:10) - Baixa potência</p>
                    <p className="drop-shadow-sm">• <strong>CH:</strong> Centesimal (1:100) - Média potência</p>
                    <p className="drop-shadow-sm">• <strong>M:</strong> Milesimal (M=1000, 10M=10000) - Alta potência</p>
                  </div>
                </div>
              </div>

              {/* Tipo de Dosagem */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Tipo de Dosagem
                </label>
                <div className="flex gap-3" data-tutorial-anchor="med-dosage-type">
                  <button
                    onClick={() => { setNewTreatment((prev) => ({ ...prev, dosageType: 'globules', dosage: '5' })); try { window.dispatchEvent(new CustomEvent('tutorial:med:dosage-type-selected')); } catch { /* ignore */ } }}
                    className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-colors ${
                      newTreatment.dosageType === 'globules'
                        ? 'bg-green-100 text-green-600 border-2 border-green-300'
                        : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                    }`}
                  >
                    <i className="ri-medicine-bottle-line mr-2"></i>
                    Glóbulos
                  </button>
                  <button
                    onClick={() => { setNewTreatment((prev) => ({ ...prev, dosageType: 'drops', dosage: '10' })); try { window.dispatchEvent(new CustomEvent('tutorial:med:dosage-type-selected')); } catch { /* ignore */ } }}
                    className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-colors ${
                      newTreatment.dosageType === 'drops'
                        ? 'bg-green-100 text-green-600 border-2 border-green-300'
                        : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                    }`}
                  >
                    <i className="ri-drop-line mr-2"></i>
                    Gotas
                  </button>
                </div>
              </div>

              {/* Dosagem */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {newTreatment.dosageType === 'globules' ? 'Quantidade de Glóbulos' : 'Quantidade de Gotas'}
                </label>
                
                <div className="flex items-center justify-center bg-gray-50 rounded-2xl p-2" data-tutorial-anchor="med-dosage-quantity">
                  <button
                    type="button"
                    onClick={() => { decrementDosage(); try { window.dispatchEvent(new CustomEvent('tutorial:med:dosage-quantity-filled')); } catch { /* ignore */ } }}
                    disabled={parseInt(newTreatment.dosage) <= 1}
                    className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <i className="ri-subtract-line text-gray-600"></i>
                  </button>
                  
                  <div className="flex-1 text-center mx-4">
                    <input
                      type="number"
                      value={newTreatment.dosage}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        const clampedValue = Math.max(1, Math.min(20, value));
                        setNewTreatment(prev => ({ ...prev, dosage: clampedValue.toString() }));
                        try { window.dispatchEvent(new CustomEvent('tutorial:med:dosage-quantity-filled')); } catch { /* ignore */ }
                      }}
                      min="1"
                      max="20"
                      className="w-full text-center text-2xl font-bold text-gray-800 bg-transparent border-none outline-none"
                    />
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => { incrementDosage(); try { window.dispatchEvent(new CustomEvent('tutorial:med:dosage-quantity-filled')); } catch { /* ignore */ } }}
                    disabled={parseInt(newTreatment.dosage) >= 20}
                    className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <i className="ri-add-line text-gray-600"></i>
                  </button>
                </div>
                
                <p className="text-xs text-gray-500 mt-2 text-center drop-shadow-sm">
                  {newTreatment.dosageType === 'globules' ? 'Quantidade de glóbulosos' : 'Quantidade de gotas'} (1 a 20)
                </p>
              </div>

              {/* Data de Início do Tratamento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Data de Início do Tratamento *
                </label>
                
                <div className="relative" data-tutorial-anchor="med-start-date">
                  <input
                    type="date"
                    value={newTreatment.startDate}
                    onChange={(e) => { setNewTreatment(prev => ({ ...prev, startDate: e.target.value })); try { window.dispatchEvent(new CustomEvent('tutorial:med:start-date-filled')); } catch { /* ignore */ } }}
                    min={getTodayBRT()}
                    className="w-full p-4 border border-gray-200 rounded-2xl text-sm focus:border-green-300 focus:outline-none pr-12"
                  />
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <i className="ri-calendar-line text-gray-400 text-xl"></i>
                  </div>
                </div>
                
                {/* Opções de data rápida */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setNewTreatment(prev => ({ 
                        ...prev, 
                        startDate: getTodayBRT() 
                      }));
                      try { window.dispatchEvent(new CustomEvent('tutorial:med:start-date-filled')); } catch { /* ignore */ }
                    }}
                    className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                      newTreatment.startDate === getTodayBRT()
                        ? 'bg-green-100 text-green-600 border border-green-300'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <i className="ri-calendar-today-line mr-1"></i>
                    Hoje
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      const tomorrowYmd = addDaysBRTFrom(getTodayBRT(), 1);
                      setNewTreatment(prev => ({ 
                        ...prev, 
                        startDate: tomorrowYmd 
                      }));
                      try { window.dispatchEvent(new CustomEvent('tutorial:med:start-date-filled')); } catch { /* ignore */ }
                    }}
                    className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                      newTreatment.startDate === addDaysBRTFrom(getTodayBRT(), 1)
                        ? 'bg-green-100 text-green-600 border border-green-300'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <i className="ri-calendar-event-line mr-1"></i>
                    Amanhã
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      const nextMon = nextMondayBRT(getTodayBRT());
                      setNewTreatment(prev => ({ 
                        ...prev, 
                        startDate: nextMon 
                      }));
                      try { window.dispatchEvent(new CustomEvent('tutorial:med:start-date-filled')); } catch { /* ignore */ }
                    }}
                    className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                      newTreatment.startDate === nextMondayBRT(getTodayBRT())
                        ? 'bg-green-100 text-green-600 border border-green-300'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <i className="ri-calendar-2-line mr-1"></i>
                    Próxima Segunda
                  </button>
                </div>
                
                {/* Informações sobre a data escolhida */}
                <div className="mt-3 p-3 bg-blue-50 rounded-xl">
                  <div className="flex items-center text-blue-700">
                    <i className="ri-information-line mr-2 text-sm"></i>
                    <span className="text-xs drop-shadow-sm">
                      {(() => {
                        const todayYmd = getTodayBRT();
                        const diffDays = (() => {
                          if (newTreatment.startDate === todayYmd) return 0;
                          if (newTreatment.startDate === addDaysBRTFrom(todayYmd, 1)) return 1;
                          const base = new Date(`${todayYmd}T12:00:00-03:00`);
                          const sel = new Date(`${newTreatment.startDate}T12:00:00-03:00`);
                          const ms = sel.getTime() - base.getTime();
                          return Math.round(ms / (1000 * 60 * 60 * 24));
                        })();
                        
                        if (diffDays === 0) return 'O tratamento começará hoje';
                        if (diffDays === 1) return 'O tratamento começará amanhã';
                        if (diffDays > 1) return `O tratamento começará em ${diffDays} dias`;
                        return 'Data inválida';
                      })()
                    }
                    </span>
                  </div>
                  <div className="text-xs text-blue-600 mt-1">
                    Esta data será usada para calcular lembretes, streaks e estatísticas do tratamento.
                  </div>
                </div>
              </div>

              {/* Frequência */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Frequência do Tratamento
                </label>
                <div className="grid grid-cols-2 gap-3" data-tutorial-anchor="med-frequency">
                  {[
                    { key: 'daily', label: 'Diário', icon: 'ri-calendar-line' },
                    { key: 'alternate', label: 'Dias Alternados', icon: 'ri-calendar-2-line' },
                    { key: 'weekly', label: 'Semanal', icon: 'ri-calendar-week-line' },
                    { key: 'custom', label: 'Personalizado', icon: 'ri-settings-3-line' },
                  ].map((freq) => (
                    <button
                      key={freq.key}
                      onClick={() => { setNewTreatment((prev) => ({ ...prev, frequency: freq.key as any })); try { window.dispatchEvent(new CustomEvent('tutorial:med:frequency-selected')); } catch { /* ignore */ } }}
                      className={`py-3 px-4 rounded-xl text-sm font-medium transition-colors flex items-center justify-center ${
                        newTreatment.frequency === freq.key
                          ? 'bg-purple-100 text-purple-600 border-2 border-purple-300'
                          : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                      }`}
                    >
                      <i className={`${freq.icon} mr-2 text-base`}></i>
                      {freq.label}
                    </button>
                  ))}
                </div>
                
                {/* Informação sobre a frequência escolhida */}
                <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                  <div className="text-xs text-gray-600">
                    {newTreatment.frequency === 'daily' && (
                      <span className="drop-shadow-sm">
                        <i className="ri-information-line mr-1"></i>
                        Medicação será tomada todos os dias nos horários definidos
                      </span>
                    )}
                    {newTreatment.frequency === 'alternate' && (
                      <span className="drop-shadow-sm">
                        <i className="ri-information-line mr-1"></i>
                        Medicação será tomada em dias alternados (dia sim, dia não)
                      </span>
                    )}
                    {newTreatment.frequency === 'weekly' && (
                      <span className="drop-shadow-sm">
                        <i className="ri-information-line mr-1"></i>
                        Medicação será tomada uma vez por semana no mesmo dia
                      </span>
                    )}
                    {newTreatment.frequency === 'custom' && (
                      <span className="drop-shadow-sm">
                        <i className="ri-information-line mr-1"></i>
                        Você poderá escolher dias específicos da semana
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Dia da Semana (Semanal) */}
              {newTreatment.frequency === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Dia da Semana
                  </label>
                  <div className="grid grid-cols-7 gap-2">
                    {weekDays.map((day, index) => (
                      <button
                        key={day}
                        onClick={() => setNewTreatment(prev => ({ ...prev, weeklyDayIndex: index }))}
                        className={`py-2 rounded-xl text-xs font-medium transition-colors ${
                          newTreatment.weeklyDayIndex === index
                            ? 'bg-purple-100 text-purple-600 border-2 border-purple-300'
                            : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                  {newTreatment.weeklyDayIndex === null && (
                    <p className="text-xs text-red-500 mt-2">
                      Selecione o dia da semana para a medicação
                    </p>
                  )}
                </div>
              )}
                            {/* Dias Personalizados */}
              {newTreatment.frequency === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Selecionar Dias da Semana
                  </label>
                  <div className="flex gap-2">
                    {weekDays.map((day) => (
                      <button
                        key={day}
                        onClick={() => toggleCustomDay(day)}
                        className={`flex-1 py-2 rounded-xl text-xs font-medium transition-colors ${
                          newTreatment.customDays.includes(day)
                            ? 'bg-purple-100 text-purple-600 border-2 border-purple-300'
                            : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Horários */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">Horários das Doses</label>
                  <button
                    onClick={addTimeSlot}
                    disabled={newTreatment.times.length >= 6}
                    className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-add-line text-green-600"></i>
                  </button>
                </div>
                <div className="space-y-3" data-tutorial-anchor="med-schedule">
                  {newTreatment.times.map((time, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <input
                        type="time"
                        value={time}
                        onChange={(e) => updateTimeSlot(index, e.target.value)}
                        className="flex-1 p-3 border border-gray-200 rounded-xl text-sm focus:border-green-300 focus:outline-none"
                      />
                      {newTreatment.times.length > 1 && (
                        <button
                          onClick={() => removeTimeSlot(index)}
                          className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center"
                        >
                          <i className="ri-subtract-line text-red-600"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Duração do Tratamento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Duração do Tratamento
                </label>

                <div className="space-y-4">
                  {/* Dias de Uso */}
                  <div data-tutorial-anchor="med-usage-days">
                                        <label className="block text-xs font-medium text-gray-600 mb-2">
                      Dias de Uso da Medicação
                      {newTreatment.frequency === 'weekly' && (
                        <span className="ml-2 text-purple-600">(Fixo: 1 dia)</span>
                      )}
                      {newTreatment.frequency === 'alternate' && (
                        <span className="ml-2 text-purple-600">(Fixo: 1 dia)</span>
                      )}
                      {newTreatment.frequency === 'custom' && (
                        <span className="ml-2 text-purple-600">(Automático: {newTreatment.customDays.length} {newTreatment.customDays.length === 1 ? 'dia' : 'dias'})</span>
                      )}
                    </label>
                    <input
                      type="number"
                      value={newTreatment.frequency === 'weekly' || newTreatment.frequency === 'alternate' ? '1' : newTreatment.usageDays}
                      onChange={(e) => { if (newTreatment.frequency !== 'weekly' && newTreatment.frequency !== 'alternate' && newTreatment.frequency !== 'custom') { setNewTreatment((prev) => ({ ...prev, usageDays: e.target.value })); try { window.dispatchEvent(new CustomEvent('tutorial:med:usage-days-filled')); } catch { /* ignore */ } } }}
                      min="1"
                      max="365"
                      disabled={newTreatment.frequency === 'weekly' || newTreatment.frequency === 'alternate' || newTreatment.frequency === 'custom'}
                      readOnly={newTreatment.frequency === 'weekly' || newTreatment.frequency === 'alternate' || newTreatment.frequency === 'custom'}
                      className={`w-full p-3 border border-gray-200 rounded-xl text-sm ${(newTreatment.frequency === 'weekly' || newTreatment.frequency === 'alternate' || newTreatment.frequency === 'custom') ? 'bg-gray-100 cursor-not-allowed' : 'focus:border-green-300 focus:outline-none'}`}
                      placeholder="Ex: 30"
                    />
                    {(newTreatment.frequency !== 'weekly' && newTreatment.frequency !== 'alternate' && newTreatment.frequency !== 'custom') && (
                      <p className="text-xs text-gray-500 mt-1 drop-shadow-sm">Quantos dias consecutivos tomar a medicação</p>
                    )}
                  </div>

                  {/* Dias de Pausa */}
                  <div data-tutorial-anchor="med-pause-days">
                                        <label className="block text-xs font-medium text-gray-600 mb-2">
                      Dias de Interrupção
                      {newTreatment.frequency === 'weekly' && (
                        <span className="ml-2 text-purple-600">(Fixo: 6 dias)</span>
                      )}
                      {newTreatment.frequency === 'alternate' && (
                        <span className="ml-2 text-purple-600">(Fixo: 1 dia)</span>
                      )}
                      {newTreatment.frequency === 'custom' && (
                        <span className="ml-2 text-purple-600">(Automático: {7 - newTreatment.customDays.length} {(7 - newTreatment.customDays.length) === 1 ? 'dia' : 'dias'})</span>
                      )}
                    </label>
                    <input
                      type="number"
                      value={newTreatment.frequency === 'weekly' ? '6' : (newTreatment.frequency === 'alternate' ? '1' : newTreatment.pauseDays)}
                      onChange={(e) => { if (newTreatment.frequency !== 'weekly' && newTreatment.frequency !== 'alternate' && newTreatment.frequency !== 'custom') { setNewTreatment((prev) => ({ ...prev, pauseDays: e.target.value })); try { window.dispatchEvent(new CustomEvent('tutorial:med:pause-days-filled')); } catch { /* ignore */ } } }}
                      min="0"
                      max="365"
                      disabled={newTreatment.frequency === 'weekly' || newTreatment.frequency === 'alternate' || newTreatment.frequency === 'custom'}
                      readOnly={newTreatment.frequency === 'weekly' || newTreatment.frequency === 'alternate' || newTreatment.frequency === 'custom'}
                      className={`w-full p-3 border border-gray-200 rounded-xl text-sm ${(newTreatment.frequency === 'weekly' || newTreatment.frequency === 'alternate' || newTreatment.frequency === 'custom') ? 'bg-gray-100 cursor-not-allowed' : 'focus:border-green-300 focus:outline-none'}`}
                      placeholder="Ex: 7"
                    />
                    {(newTreatment.frequency !== 'weekly' && newTreatment.frequency !== 'alternate' && newTreatment.frequency !== 'custom') && (
                      <p className="text-xs text-gray-500 mt-1 drop-shadow-sm">Quantos dias pausar entre os ciclos (0 = sem pausa)</p>
                    )}
                  </div>

                  {/* Período Total */}
                  <div data-tutorial-anchor="med-total-period">
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Período Total do Tratamento (meses)
                    </label>
                    <div className="flex gap-2">
                      {['1', '2', '3', '6', '9', '12'].map((months) => (
                        <button
                          key={months}
                          onClick={() => { setNewTreatment((prev) => ({ ...prev, totalPeriod: months })); try { window.dispatchEvent(new CustomEvent('tutorial:med:total-period-filled')); } catch { /* ignore */ } }}
                          className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-colors ${
                            newTreatment.totalPeriod === months
                              ? 'bg-indigo-100 text-indigo-600 border-2 border-indigo-300'
                              : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                          }`}
                        >
                          {months}m
                        </button>
                      ))}
                    </div>
                    <div className="mt-2">
                      <input
                        type="number"
                        value={newTreatment.totalPeriod}
                        onChange={(e) => { setNewTreatment((prev) => ({ ...prev, totalPeriod: e.target.value })); try { window.dispatchEvent(new CustomEvent('tutorial:med:total-period-filled')); } catch { /* ignore */ } }}
                        min="1"
                        max="24"
                        className="w-full p-2 border border-gray-200 rounded-lg text-xs focus:border-indigo-300 focus:outline-none"
                        placeholder="Ou digite o número de meses"
                      />
                    </div>
                  </div>

                  {/* Resumo do Ciclo */}
                  <div className="bg-blue-50 p-4 rounded-xl" data-tutorial-anchor="cycle-summary">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Resumo do Ciclo:</h4>
                    <div className="text-xs text-blue-700 space-y-1">
                      {/* Data de Início */}
                      <p className="drop-shadow-sm">
                        • <strong>Início:</strong> {(() => {
                          const selectedDate = new Date(newTreatment.startDate + 'T00:00:00');
                          const today = new Date();
                          const diffTime = selectedDate.getTime() - today.getTime();
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          
                          const dateStr = selectedDate.toLocaleDateString('pt-BR');
                          
                          if (diffDays === 0) return `${dateStr} (hoje)`;
                          if (diffDays === 1) return `${dateStr} (amanhã)`;
                          if (diffDays > 1) return `${dateStr} (em ${diffDays} dias)`;
                          return dateStr;
                        })()
                      }
                      </p>

                      {/* Frequência */}
                      <p className="drop-shadow-sm">
                        • <strong>Frequência:</strong> {(() => {
                          switch (newTreatment.frequency) {
                            case 'daily':
                              return 'Todos os dias';
                            case 'alternate':
                              return 'Dias alternados (dia sim, dia não)';
                            case 'weekly':
                              return 'Uma vez por semana';
                            case 'custom':
                              return newTreatment.customDays.length > 0 
                                ? `Dias específicos: ${newTreatment.customDays.join(', ')}`
                                : 'Dias personalizados (selecione os dias)';
                            default:
                              return 'Diário';
                          }
                        })()
                      }
                      </p>

                      {/* Horários */}
                      <p className="drop-shadow-sm">
                        • <strong>Horários:</strong> {newTreatment.times.join(', ')} ({newTreatment.times.length} {newTreatment.times.length === 1 ? 'vez' : 'vezes'} ao dia)
                      </p>

                      {/* Dosagem */}
                      <p className="drop-shadow-sm">
                        • <strong>Dosagem:</strong> {newTreatment.dosage} {newTreatment.dosageType === 'globules' ? 'glóbulos' : 'gotas'} por dose
                      </p>

                      {/* Duração do uso */}
                      <p className="drop-shadow-sm">
                        • <strong>{newTreatment.usageDays} dias</strong> tomando a medicação
                      </p>
                      
                      {/* Pausa */}
                      {parseInt(newTreatment.pauseDays) > 0 && (
                        <p className="drop-shadow-sm">
                          • <strong>{newTreatment.pauseDays} dias</strong> de pausa
                        </p>
                      )}
                      
                      {/* Período total */}
                      <p className="drop-shadow-sm">
                        • <strong>Duração total:</strong> {newTreatment.totalPeriod} {parseInt(newTreatment.totalPeriod) === 1 ? 'mês' : 'meses'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Observações */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Observações (opcional)
                </label>
                <textarea
                  value={newTreatment.notes}
                  onChange={(e) => setNewTreatment((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Instruções especiais, recomendações médicas..."
                  className="w-full h-20 p-4 border border-gray-200 rounded-2xl resize-none text-sm focus:border-green-300 focus:outline-none"
                  maxLength={200}
                />
                <p className="text-xs text-gray-500 mt-2 text-right drop-shadow-sm">
                {newTreatment.notes.length}/200
              </p>
              </div>
            </div>

            {/* Botões */}
            <div className="sticky bottom-0 bg-white p-6 border-t border-gray-100 rounded-b-3xl">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowTreatmentModal(false)}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-2xl font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveTreatment}
                  disabled={!newTreatment.medicationName || !newTreatment.potencyNumber || !newTreatment.potencyType || !newTreatment.startDate || savingTreatment}
                  className="flex-1 py-3 bg-green-500 text-white rounded-2xl font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-tutorial-anchor="btn-save-medication"
                >
                  {savingTreatment && (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  )}
                  {savingTreatment 
                    ? 'Salvando...' 
                    : editingMedication ? 'Atualizar Medicação' : 'Salvar Medicação'
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Modal */}
      {showQuizModal && (
        <QuizModal 
          isOpen={showQuizModal}
          memberId={member.id}
          onClose={() => setShowQuizModal(false)}
          onQuizComplete={async (summary) => {
            console.log('🎯 [Quiz] Quiz completado:', summary);
            
            // Salvar histórico do quiz no localStorage PRIMEIRO
            const quizHistoryKey = `quiz_history_${memberId}`;
            const existingHistory = localStorage.getItem(quizHistoryKey);
            const history = existingHistory ? JSON.parse(existingHistory) : [];
            history.push({
              date: summary.date,
              questionsAnswered: summary.questionsAnswered,
              correctAnswers: summary.correctAnswers,
              accuracy: summary.accuracy,
              totalPoints: summary.totalPoints
            });
            localStorage.setItem(quizHistoryKey, JSON.stringify(history));
            console.log('💾 [Quiz] Histórico salvo:', history.length, 'quizzes');
            
            // Salvar pontos ANTES
          const oldPoints = memberPoints?.totalPoints || 0;
          
          // Buscar pontos atualizados
          const updatedPoints = await pointsService.getMemberPoints(memberId!);
          setMemberPoints(updatedPoints);
          dispatchPointsUpdated();
          
          if (updatedPoints) {
            // Verificar conquistas
            await checkAchievementOnPoints(
              memberId!,
              oldPoints,
              updatedPoints.totalPoints,
              'quiz_completed'
            );

            // Verificar e desbloquear badges automaticamente
            const pointsBeforeBadges = updatedPoints.totalPoints;
            await checkAndUnlockBadges(memberId!, setBadgesCount);

            // Refetch curto pós-badges (absorver latência das triggers)
            let finalPoints: MemberPoints | null = updatedPoints;
            try {
              let tries = 0;
              let afterBadges: MemberPoints | null = null;
              while (tries < 3) {
                await new Promise(r => setTimeout(r, 250));
                afterBadges = await pointsService.getMemberPoints(memberId!);
                if (afterBadges && afterBadges.totalPoints > pointsBeforeBadges) break;
                tries++;
              }
              if (afterBadges && afterBadges.totalPoints !== pointsBeforeBadges) {
                finalPoints = afterBadges;
                setMemberPoints(afterBadges);
                dispatchPointsUpdated();
              }
            } catch (err) {
              console.warn('⚠️ [Quiz] Falha no refetch pós-badges:', err);
            }

            if (finalPoints) {
              // Atualizar título se mudou de nível
              const newTitle = await pointsService.getLevelTitle(finalPoints.totalPoints, 'member');
              if (newTitle) {
                setMemberLevelTitle(newTitle);
              } else {
                const fallbackTitle = await pointsService.getLevelTitleByLevel(finalPoints.level ?? 1, 'member');
                setMemberLevelTitle(fallbackTitle);
              }

              // Refletir total de pontos no estado do membro
              setMember(prev => ({
                ...prev,
                streak: finalPoints.currentTreatmentStreak ?? prev.streak,
                points: finalPoints.totalPoints ?? prev.points
              }));
              dispatchPointsUpdated();

              console.log('✅ [Quiz] Pontos atualizados:', oldPoints, '→', finalPoints.totalPoints);
            }
          }
          }}
        />
      )}

      {/* Treatment Summary Modal */}
      {showTreatmentSummaryModal && summaryMedicationId && (
        <TreatmentSummaryModal
          isOpen={showTreatmentSummaryModal}
          memberId={member.id}
          memberName={member.name}
          medicationId={summaryMedicationId}
          onClose={async () => {
            setShowTreatmentSummaryModal(false);
            try { 
              await TreatmentSummaryService.markSummaryShown(member.id, summaryMedicationId!); 
            } catch (error) {
              console.warn('[MemberDashboard] Falha ao marcar sumário como visto:', error);
            }
            setSummaryMedicationId(null);
          }}
        />
      )}

      {/* Quiz Summary Modal */}
      {showQuizSummaryModal && (
        <QuizSummaryModal 
          isOpen={showQuizSummaryModal}
          memberId={member.id}
          memberName={member.name}
          onClose={() => setShowQuizSummaryModal(false)}
        />
      )}

      {/* Health History Modal */}
      <HealthHistoryModal
        isOpen={showHealthHistoryModal}
        memberId={member.id}
        memberName={member.name}
        onClose={() => setShowHealthHistoryModal(false)}
      />

      {/* Points History Modal */}
      <PointsHistoryModal
        isOpen={showPointsHistoryModal}
        memberId={member.id}
        memberName={member.name}
        onClose={() => setShowPointsHistoryModal(false)}
      />

      {/* Achievement Notification */}
      {achievement && (
        <AchievementNotification
          show={showAchievement}
          title={achievement.title}
          description={achievement.description}
          points={achievement.points}
          icon={achievement.icon}
          metadata={achievement.metadata}
          onClose={closeAchievement}
        />
      )}

      {/* Badge Gallery */}
      <BadgeGallery
        memberId={memberId!}
        isOpen={showBadgeGallery}
        onClose={() => setShowBadgeGallery(false)}
      />

      {/* Badge Notification */}
      {badgeNotification && badgeNotification.show && (
        <BadgeNotification
          badge={badgeNotification.badge}
          show={badgeNotification.show}
          onClose={handleCloseBadge}
        />
      )}

      {/* Upgrade Modal - Análise de Prescrição */}
      {showPrescriptionUpgradeModal && (
        <UpgradeModal
          featureKey="prescription_analysis"
          featureName="Análise de Prescrição"
          currentPlan={subscription?.plan_tier || 'free'}
          usage={prescriptionFeatureUsage}
          onClose={() => setShowPrescriptionUpgradeModal(false)}
        />
      )}

      <BottomNavigation />
    </div>
  );
}




















